/**
 * ============================================================================
 * ADMIN PAGE - Configuration & Management Console
 * ============================================================================
 * Central administration interface for NSAS TOOLBOX
 * 
 * TABS (12 total):
 * ----------------
 * 1. Ops Dashboard     - Flight overview, defects reporting
 * 2. Fleet Status      - Contract tracking, hours analytics
 * 3. Customer Downloads- PIN generation, video sharing
 * 4. Aircraft Tracker  - ADS-B tracking configuration
 * 5. API Configuration - External API keys management
 * 6. FTX Designators   - Mission designator setup
 * 7. Route Files       - KML/KMZ flight route management
 * 8. Aircraft          - Aircraft types list
 * 9. Registrations     - Aircraft registrations
 * 10. Pilots           - Pilot list management
 * 11. Liaison Officers - Liaison officer management
 * 12. Weather Decisions- OMA weather criteria
 * 
 * SECTIONS IN THIS FILE:
 * ----------------------
 * [1] IMPORTS & CONSTANTS          - Lines 1-80
 * [2] STATE DEFINITIONS            - Lines 80-200
 * [3] EFFECTS (data loading)       - Lines 200-500
 * [4] HANDLER FUNCTIONS            - Lines 500-970
 * [5] TAB DEFINITIONS              - Lines 970-990
 * [6] RENDER - TAB NAVIGATION      - Lines 990-1080
 * [7] RENDER - OPS DASHBOARD       - Lines 1080-1430
 * [8] RENDER - FLEET STATUS        - Lines 1430-1600
 * [9] RENDER - CUSTOMER DOWNLOADS  - Lines 1600-1810
 * [10] RENDER - AIRCRAFT TRACKER   - Lines 1810-1930
 * [11] RENDER - API CONFIG         - Lines 1930-2100
 * [12] RENDER - FTX DESIGNATORS    - Lines 2100-2220
 * [13] RENDER - ROUTES             - Lines 2220-2330
 * [14] RENDER - SIMPLE LISTS       - Lines 2330-2700
 * [15] RENDER - WEATHER DECISIONS  - Lines 2700-3100
 * [16] DIALOGS & MODALS            - Lines 3100-3300
 * 
 * ============================================================================
 */

import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Plane,
  FileText,
  Users,
  MapPinned,
  Upload,
  Plus,
  Trash2,
  Loader2,
  ArrowLeft,
  Settings,
  Link,
  Map,
  Navigation,
  Info,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Wind,
  CloudRain,
  Thermometer,
  Eye,
  Cloud,
  Zap,
  RefreshCw,
  PlusCircle,
  Monitor,
  X,
  BarChart3,
  Share2,
  Copy,
  Clock,
  Download,
  RotateCcw,
  Radar,
  Edit2,
  Key,
  ExternalLink,
  Shield,
  Database,
  Wifi,
  Video,
  Mail,
  TestTube,
  Router,
  Network,
  Radio,
  Globe
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  WEATHER_CRITERIA,
  DECISION_CRITERIA,
  PATROL_EFFECTIVENESS,
  DATA_SOURCES,
  TECHNICAL_PARAMS,
} from "../config/weatherDecisionCriteria";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTheme } from "@/context/ThemeContext";
import { Sun, Moon } from "lucide-react";
import "./AdminPage.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Admin password
const ADMIN_PASSWORD = "diamond";

// =============================================================================
// [2] STATE DEFINITIONS
// =============================================================================

export default function AdminPage() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [authenticated, setAuthenticated] = useState(true); // No second password gate needed
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("opsDashboard");
  const [adminData, setAdminData] = useState({
    aircraft: [],
    registrations: [],
    pilots: [],
    liaisonOfficers: [],
    ftxDesignators: [],
    routes: []
  });
  const [newItem, setNewItem] = useState("");
  const [newItemExtra, setNewItemExtra] = useState("");
  const [uploadingFile, setUploadingFile] = useState(false);
  
  // Ops Dashboard state
  const [opsFlights, setOpsFlights] = useState([]);
  const [opsDefects, setOpsDefects] = useState([]);
  const [opsLoading, setOpsLoading] = useState(false);
  const [opsLastRefresh, setOpsLastRefresh] = useState(null);
  const [defectForm, setDefectForm] = useState({
    aircraft_reg: '',
    description: '',
    priority: 'MEDIUM',
    reported_by: '',
    is_highlighted: true
  });
  const [submittingDefect, setSubmittingDefect] = useState(false);
  
  // System Health state
  const [systemHealth, setSystemHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthLastRefresh, setHealthLastRefresh] = useState(null);
  
  // Fleet Status state
  const [fleetConfigs, setFleetConfigs] = useState([]);
  const [fleetLoading, setFleetLoading] = useState(false);
  const [editingFleetConfig, setEditingFleetConfig] = useState(null);
  const [fleetConfigForm, setFleetConfigForm] = useState({
    callsign: '',
    registration: '',
    aircraft_id: '',
    aircraft_type: '',
    deployment: '',
    base: '',
    mission_id: '',
    contract: {
      start_date: '',
      end_date: '',
      contracted_days: '',
      contracted_hours: ''
    },
    maintenance: {
      type: '',
      last_maintenance_hours: '',
      next_maintenance_at: '',
      margin: '',
      estimated_duration_days: ''
    }
  });
  const [savingFleetConfig, setSavingFleetConfig] = useState(false);
  const [showFleetConfigDialog, setShowFleetConfigDialog] = useState(false);
  
  // SkyLegs Cache state
  const [cacheStatus, setCacheStatus] = useState(null);
  const [cacheLoading, setCacheLoading] = useState(false);
  
  // Customer Downloads state
  const [customerPins, setCustomerPins] = useState([]);
  const [availableVideos, setAvailableVideos] = useState([]);
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [customerDownloadsLoading, setCustomerDownloadsLoading] = useState(false);
  const [creatingPin, setCreatingPin] = useState(false);
  const [newPinResult, setNewPinResult] = useState(null);
  
  // FTX Designator form state
  const [showFtxDialog, setShowFtxDialog] = useState(false);
  const [editingFtx, setEditingFtx] = useState(null);
  const [ftxForm, setFtxForm] = useState({
    name: "",
    routeFile: "",
    routeFiles: [],  // Support multiple route files
    description: "",
    loEmail: "",
    airports: [{ icao: "", name: "", lat: "", lon: "" }],
    videoStreamUrl: "",
    excludeFromWeather: false,
    excludeFromSkylegs: false,
    isExternal: false,
    adsbIcaoHex: ""  // For tracking external aircraft via ADS-B
  });

  // Aircraft Tracker state
  const [trackedAircraft, setTrackedAircraft] = useState([]);
  const [trackedAircraftLoading, setTrackedAircraftLoading] = useState(false);
  const [showAircraftDialog, setShowAircraftDialog] = useState(false);
  const [editingAircraft, setEditingAircraft] = useState(null);
  const [aircraftForm, setAircraftForm] = useState({
    icao_hex: "",
    registration: "",
    callsign: "",
    color: "#FF6B00",
    active: true
  });
  const [savingAircraft, setSavingAircraft] = useState(false);

  // API Configuration state
  const [apiConfigs, setApiConfigs] = useState([]);
  const [apiCategories, setApiCategories] = useState({});
  const [apiConfigsLoading, setApiConfigsLoading] = useState(false);
  const [editingApiKey, setEditingApiKey] = useState(null);

  // Weather Model state
  const [weatherModel, setWeatherModel] = useState(null);
  const [weatherModelLoading, setWeatherModelLoading] = useState(false);
  const [newApiValue, setNewApiValue] = useState("");
  const [testingApiKey, setTestingApiKey] = useState(null);

  // ===========================================================================
  // [3] DATA FETCHING FUNCTIONS
  // ===========================================================================

  const handleLogin = () => {
    if (password === ADMIN_PASSWORD) {
      setAuthenticated(true);
      fetchAdminData();
      toast.success("Admin access granted");
    } else {
      toast.error("Invalid password");
    }
  };

  const fetchAdminData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/config`);
      setAdminData(response.data);
    } catch (error) {
      console.error("Failed to fetch admin data:", error);
      // Initialize with defaults if no data exists
      setAdminData({
        aircraft: ["Diamond DA42 MPP", "Diamond DA62 MPP"],
        registrations: ["OO-MSA", "OO-MSB", "OO-MSC", "OO-MSD"],
        pilots: ["FDT", "LLU", "JDW", "PVD"],
        liaisonOfficers: ["XDU 🥳", "MDC", "LLU"],
        ftxDesignators: [],
        routes: []
      });
    } finally {
      setLoading(false);
    }
  };

  // API Configuration functions
  const fetchApiConfigs = async () => {
    setApiConfigsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/api-configs`);
      setApiConfigs(response.data.configs || []);
      setApiCategories(response.data.categories || {});
    } catch (error) {
      console.error('Error fetching API configs:', error);
      toast.error('Failed to fetch API configurations');
    } finally {
      setApiConfigsLoading(false);
    }
  };

  const handleUpdateApiConfig = async (key) => {
    try {
      await axios.put(`${API}/admin/api-configs/${key}`, { value: newApiValue });
      toast.success('API configuration updated');
      setEditingApiKey(null);
      setNewApiValue("");
      fetchApiConfigs();
    } catch (error) {
      console.error('Error updating API config:', error);
      toast.error('Failed to update API configuration');
    }
  };

  const handleTestApiConfig = async (key) => {
    setTestingApiKey(key);
    try {
      const response = await axios.get(`${API}/admin/api-configs/${key}/test`);
      if (response.data.status === 'ok') {
        toast.success(`${response.data.name}: ${response.data.message}`);
      } else if (response.data.status === 'error') {
        toast.error(`${response.data.name}: ${response.data.message}`);
      } else {
        toast.info(`${response.data.name}: ${response.data.message}`);
      }
    } catch (error) {
      console.error('Error testing API config:', error);
      toast.error('Failed to test API configuration');
    } finally {
      setTestingApiKey(null);
    }
  };

  const handleResetApiConfig = async (key) => {
    try {
      await axios.delete(`${API}/admin/api-configs/${key}`);
      toast.success('API configuration reset to default');
      fetchApiConfigs();
    } catch (error) {
      console.error('Error resetting API config:', error);
      toast.error('Failed to reset API configuration');
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Aircraft Tracking': return Radar;
      case 'Weather': return Cloud;
      case 'Flight Operations': return Plane;
      case 'Video Streaming': return Video;
      case 'Email': return Mail;
      default: return Database;
    }
  };

  // Ops Dashboard functions
  const fetchOpsData = async () => {
    setOpsLoading(true);
    try {
      const [flightsRes, defectsRes, cacheRes] = await Promise.all([
        axios.get(`${API}/ops/skylegs/flights`),
        axios.get(`${API}/ops/defects`),
        axios.get(`${API}/ops/skylegs/cache/status`)
      ]);
      setOpsFlights(flightsRes.data || []);
      setOpsDefects(defectsRes.data || []);
      setCacheStatus(cacheRes.data || null);
      setOpsLastRefresh(new Date());
    } catch (error) {
      console.error('Error fetching ops data:', error);
      toast.error('Failed to refresh ops data');
    } finally {
      setOpsLoading(false);
    }
  };

  const handleRefreshCache = async (force = false) => {
    setCacheLoading(true);
    try {
      const response = await axios.post(`${API}/ops/skylegs/cache/refresh?force=${force}`);
      toast.success(`Cache ${force ? 'fully refreshed' : 'updated'}: ${response.data.flights_after} flights`);
      // Refresh cache status
      const cacheRes = await axios.get(`${API}/ops/skylegs/cache/status`);
      setCacheStatus(cacheRes.data || null);
    } catch (error) {
      console.error('Error refreshing cache:', error);
      toast.error('Failed to refresh cache');
    } finally {
      setCacheLoading(false);
    }
  };

  const handleSubmitDefect = async (e) => {
    e.preventDefault();
    if (!defectForm.aircraft_reg || !defectForm.description || !defectForm.reported_by) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmittingDefect(true);
    try {
      await axios.post(`${API}/ops/defects`, defectForm);
      setDefectForm({
        aircraft_reg: '',
        description: '',
        priority: 'MEDIUM',
        reported_by: '',
        is_highlighted: true
      });
      toast.success('Defect reported successfully');
      fetchOpsData();
    } catch (error) {
      toast.error('Failed to report defect');
    } finally {
      setSubmittingDefect(false);
    }
  };

  const handleDeleteDefect = async (defectId) => {
    try {
      await axios.delete(`${API}/ops/defects/${defectId}`);
      toast.success('Defect deleted');
      fetchOpsData();
    } catch (error) {
      toast.error('Failed to delete defect');
    }
  };

  // System Health functions
  const fetchSystemHealth = async () => {
    setHealthLoading(true);
    try {
      const response = await axios.get(`${API}/admin/system-health`);
      setSystemHealth(response.data);
      setHealthLastRefresh(new Date());
    } catch (error) {
      console.error('Error fetching system health:', error);
      toast.error('Failed to fetch system health');
    } finally {
      setHealthLoading(false);
    }
  };

  // Manually refresh SkyLegs cache
  const refreshSkyLegsCache = async () => {
    setCacheLoading(true);
    try {
      const response = await axios.post(`${API}/ops/skylegs/cache/refresh?force=true`);
      if (response.data.success) {
        toast.success(`Cache refreshed: ${response.data.flights_after} flights cached`);
        // Refresh system health to show updated cache status
        await fetchSystemHealth();
      } else {
        toast.error(response.data.error || 'Failed to refresh cache');
      }
    } catch (error) {
      console.error('Error refreshing cache:', error);
      toast.error('Failed to refresh SkyLegs cache');
    } finally {
      setCacheLoading(false);
    }
  };

  // ===========================================================================
  // [4] EFFECTS - Auto-loading data based on active tab
  // ===========================================================================

  // Weather Model functions
  const fetchWeatherModel = async () => {
    setWeatherModelLoading(true);
    try {
      const response = await axios.get(`${API}/admin/weather-model`);
      setWeatherModel(response.data);
    } catch (error) {
      console.error('Error fetching weather model:', error);
    } finally {
      setWeatherModelLoading(false);
    }
  };

  const switchWeatherModel = async (modelId) => {
    setWeatherModelLoading(true);
    try {
      const response = await axios.post(`${API}/admin/weather-model/${modelId}`);
      if (response.data.success) {
        setWeatherModel(prev => ({
          ...prev,
          active_model: modelId,
          model_details: response.data.model_details
        }));
        toast.success(response.data.message);
      } else {
        toast.error(response.data.error || 'Failed to switch model');
      }
    } catch (error) {
      console.error('Error switching weather model:', error);
      toast.error('Failed to switch weather model');
    } finally {
      setWeatherModelLoading(false);
    }
  };

  // Load system health when tab is active
  useEffect(() => {
    if (authenticated && activeTab === 'opsDashboard') {
      fetchSystemHealth();
    }
  }, [authenticated, activeTab]);

  // Load weather model when Weather Decisions tab is active
  useEffect(() => {
    if (authenticated && activeTab === 'weatherDecisions') {
      fetchWeatherModel();
    }
  }, [authenticated, activeTab]);

  // Fetch admin data on mount
  useEffect(() => {
    fetchAdminData();
  }, []);

  // Fetch fleet configurations from API (config only, not live status)
  const fetchFleetConfigs = async () => {
    setFleetLoading(true);
    try {
      const configsRes = await axios.get(`${API}/fleet/config`);
      setFleetConfigs(configsRes.data || []);
    } catch (error) {
      console.error('Error fetching fleet data:', error);
      toast.error('Failed to load fleet configurations');
    } finally {
      setFleetLoading(false);
    }
  };

  // Load fleet data when tab is active
  useEffect(() => {
    if (authenticated && activeTab === 'fleetStatus') {
      fetchFleetConfigs();
    }
  }, [authenticated, activeTab]);

  // Customer Downloads functions
  const fetchCustomerDownloadsData = async () => {
    setCustomerDownloadsLoading(true);
    try {
      const [pinsRes, videosRes] = await Promise.all([
        axios.get(`${API}/customer-download/pins`),
        axios.get(`${API}/customer-download/available-videos`)
      ]);
      setCustomerPins(pinsRes.data || []);
      setAvailableVideos(videosRes.data || []);
    } catch (error) {
      console.error('Error fetching customer downloads data:', error);
      toast.error('Failed to load customer downloads data');
    } finally {
      setCustomerDownloadsLoading(false);
    }
  };

  // Load customer downloads data when tab is active
  useEffect(() => {
    if (authenticated && activeTab === 'customerDownloads') {
      fetchCustomerDownloadsData();
    }
  }, [authenticated, activeTab]);

  const handleCreatePin = async () => {
    if (selectedVideos.length === 0) {
      toast.error('Please select at least one video');
      return;
    }
    
    setCreatingPin(true);
    try {
      const response = await axios.post(`${API}/customer-download/create-pin`, {
        videos: selectedVideos
      });
      setNewPinResult(response.data);
      setSelectedVideos([]);
      fetchCustomerDownloadsData();
      toast.success('PIN created successfully');
    } catch (error) {
      console.error('Error creating PIN:', error);
      toast.error(error.response?.data?.detail || 'Failed to create PIN');
    } finally {
      setCreatingPin(false);
    }
  };

  const handleRevokePin = async (pin) => {
    if (!window.confirm('Are you sure you want to revoke this PIN?')) return;
    
    try {
      await axios.delete(`${API}/customer-download/pins/${pin}`);
      fetchCustomerDownloadsData();
      toast.success('PIN revoked');
    } catch (error) {
      toast.error('Failed to revoke PIN');
    }
  };

  const handleReinstatePin = async (pin) => {
    try {
      await axios.post(`${API}/customer-download/reinstate/${pin}`);
      fetchCustomerDownloadsData();
      toast.success('PIN reinstated with new 36hr window');
    } catch (error) {
      toast.error('Failed to reinstate PIN');
    }
  };

  // Aircraft Tracker functions
  const fetchTrackedAircraft = async () => {
    setTrackedAircraftLoading(true);
    try {
      const response = await axios.get(`${API}/aircraft/configured`);
      setTrackedAircraft(response.data || []);
    } catch (error) {
      console.error('Error fetching tracked aircraft:', error);
      toast.error('Failed to load tracked aircraft');
    } finally {
      setTrackedAircraftLoading(false);
    }
  };

  // Load tracked aircraft when tab is active
  useEffect(() => {
    if (authenticated && activeTab === 'aircraftTracker') {
      fetchTrackedAircraft();
    }
  }, [authenticated, activeTab]);

  // Fetch API configs when tab is selected
  useEffect(() => {
    if (authenticated && activeTab === 'apiConfig') {
      fetchApiConfigs();
    }
  }, [authenticated, activeTab]);

  // ===========================================================================
  // [5] HANDLER FUNCTIONS - Form submissions, CRUD operations
  // ===========================================================================

  const handleOpenAircraftDialog = (aircraft = null) => {
    if (aircraft) {
      setEditingAircraft(aircraft);
      setAircraftForm({
        icao_hex: aircraft.icao_hex || "",
        registration: aircraft.registration || "",
        callsign: aircraft.callsign || "",
        color: aircraft.color || "#FF6B00",
        active: aircraft.active !== false
      });
    } else {
      setEditingAircraft(null);
      setAircraftForm({
        icao_hex: "",
        registration: "",
        callsign: "",
        color: "#FF6B00",
        active: true
      });
    }
    setShowAircraftDialog(true);
  };

  const handleSaveAircraft = async () => {
    if (!aircraftForm.icao_hex || !aircraftForm.registration || !aircraftForm.callsign) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSavingAircraft(true);
    try {
      if (editingAircraft) {
        await axios.put(`${API}/aircraft/configured/${editingAircraft.icao_hex}`, aircraftForm);
        toast.success('Aircraft updated successfully');
      } else {
        await axios.post(`${API}/aircraft/configured`, aircraftForm);
        toast.success('Aircraft added successfully');
      }
      setShowAircraftDialog(false);
      fetchTrackedAircraft();
    } catch (error) {
      console.error('Error saving aircraft:', error);
      toast.error(error.response?.data?.detail || 'Failed to save aircraft');
    } finally {
      setSavingAircraft(false);
    }
  };

  const handleDeleteAircraft = async (icaoHex) => {
    if (!window.confirm('Are you sure you want to remove this aircraft from tracking?')) return;

    try {
      await axios.delete(`${API}/aircraft/configured/${icaoHex}`);
      toast.success('Aircraft removed from tracking');
      fetchTrackedAircraft();
    } catch (error) {
      console.error('Error deleting aircraft:', error);
      toast.error('Failed to remove aircraft');
    }
  };

  const handleToggleAircraftActive = async (aircraft) => {
    try {
      await axios.put(`${API}/aircraft/configured/${aircraft.icao_hex}`, {
        ...aircraft,
        active: !aircraft.active
      });
      toast.success(`Aircraft ${aircraft.active ? 'deactivated' : 'activated'}`);
      fetchTrackedAircraft();
    } catch (error) {
      toast.error('Failed to update aircraft status');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes < 0) return "Unknown";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  const getTimeRemaining = (expiresAt) => {
    if (!expiresAt) return null;
    const expires = new Date(expiresAt);
    const now = new Date();
    const diff = expires - now;
    
    if (diff <= 0) return "Expired";
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    return `${hours}h ${minutes}m`;
  };

  // Open edit dialog for fleet config
  const handleEditFleetConfig = (config) => {
    setEditingFleetConfig(config.callsign);
    setFleetConfigForm({
      callsign: config.callsign || '',
      registration: config.registration || '',
      aircraft_id: config.aircraft_id?.toString() || '',
      aircraft_type: config.aircraft_type || '',
      deployment: config.deployment || '',
      base: config.base || '',
      mission_id: config.mission_id?.toString() || '',
      contract: {
        start_date: config.contract?.start_date || '',
        end_date: config.contract?.end_date || '',
        contracted_days: config.contract?.contracted_days?.toString() || '',
        contracted_hours: config.contract?.contracted_hours?.toString() || ''
      },
      maintenance: {
        type: config.maintenance?.type || '',
        last_maintenance_hours: config.maintenance?.last_maintenance_hours?.toString() || '0',
        next_maintenance_at: config.maintenance?.next_maintenance_at?.toString() || '',
        margin: config.maintenance?.margin?.toString() || '10',
        estimated_duration_days: config.maintenance?.estimated_duration_days?.toString() || '3'
      }
    });
    setShowFleetConfigDialog(true);
  };

  // Reset fleet config form
  const resetFleetConfigForm = () => {
    setFleetConfigForm({
      callsign: '',
      registration: '',
      aircraft_id: '',
      aircraft_type: '',
      deployment: '',
      base: '',
      mission_id: '',
      contract: {
        start_date: '',
        end_date: '',
        contracted_days: '',
        contracted_hours: ''
      },
      maintenance: {
        type: '',
        last_maintenance_hours: '',
        next_maintenance_at: '',
        margin: '',
        estimated_duration_days: ''
      }
    });
    setEditingFleetConfig(null);
  };

  // Save fleet config
  const handleSaveFleetConfig = async () => {
    if (!fleetConfigForm.callsign.trim()) {
      toast.error('Callsign is required');
      return;
    }

    setSavingFleetConfig(true);
    try {
      const payload = {
        callsign: fleetConfigForm.callsign.toUpperCase().replace(/\s/g, ''),
        registration: fleetConfigForm.registration,
        aircraft_id: parseInt(fleetConfigForm.aircraft_id) || null,
        aircraft_type: fleetConfigForm.aircraft_type,
        deployment: fleetConfigForm.deployment,
        base: fleetConfigForm.base.toUpperCase(),
        mission_id: fleetConfigForm.mission_id ? parseInt(fleetConfigForm.mission_id) : null,
        contract: {
          start_date: fleetConfigForm.contract.start_date,
          end_date: fleetConfigForm.contract.end_date,
          contracted_days: parseInt(fleetConfigForm.contract.contracted_days) || 0,
          contracted_hours: parseFloat(fleetConfigForm.contract.contracted_hours) || 0
        },
        maintenance: {
          type: fleetConfigForm.maintenance.type,
          last_maintenance_hours: parseFloat(fleetConfigForm.maintenance.last_maintenance_hours) || 0,
          next_maintenance_at: parseFloat(fleetConfigForm.maintenance.next_maintenance_at) || 0,
          margin: parseInt(fleetConfigForm.maintenance.margin) || 10,
          estimated_duration_days: parseInt(fleetConfigForm.maintenance.estimated_duration_days) || 3
        }
      };

      if (editingFleetConfig) {
        // Update existing
        await axios.put(`${API}/fleet/config/${editingFleetConfig}`, payload);
        toast.success(`Updated ${payload.callsign} configuration`);
      } else {
        // Create new
        await axios.post(`${API}/fleet/config`, payload);
        toast.success(`Created ${payload.callsign} configuration`);
      }

      setShowFleetConfigDialog(false);
      resetFleetConfigForm();
      fetchFleetConfigs();
    } catch (error) {
      console.error('Error saving fleet config:', error);
      toast.error(error.response?.data?.error || 'Failed to save configuration');
    } finally {
      setSavingFleetConfig(false);
    }
  };

  const handleAddItem = async (category) => {
    if (!newItem.trim()) return;
    
    let itemToAdd;
    if (category === 'registrations' && newItemExtra) {
      itemToAdd = { name: newItem.trim(), aircraftType: newItemExtra };
    } else {
      itemToAdd = newItem.trim();
    }
    
    try {
      await axios.post(`${API}/admin/config/${category}`, { item: itemToAdd });
      setAdminData(prev => ({
        ...prev,
        [category]: [...prev[category], itemToAdd]
      }));
      setNewItem("");
      setNewItemExtra("");
      toast.success(`Added to ${category}`);
    } catch (error) {
      toast.error("Failed to add item");
    }
  };

  const handleDeleteItem = async (category, index) => {
    try {
      await axios.delete(`${API}/admin/config/${category}/${index}`);
      setAdminData(prev => ({
        ...prev,
        [category]: prev[category].filter((_, i) => i !== index)
      }));
      toast.success(`Removed from ${category}`);
    } catch (error) {
      toast.error("Failed to remove item");
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const validExtensions = ['.kml', '.kmz'];
    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    
    if (!validExtensions.includes(ext)) {
      toast.error("Please upload a KML or KMZ file");
      return;
    }
    
    setUploadingFile(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/admin/upload-route`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setAdminData(prev => ({
        ...prev,
        routes: [...prev.routes, response.data]
      }));
      
      // Show detected airports info
      const detected = response.data.detectedAirports || [];
      if (detected.length > 0) {
        const airportList = detected.slice(0, 5).map(a => a.icao).join(', ');
        const moreText = detected.length > 5 ? ` and ${detected.length - 5} more` : '';
        toast.success(
          `Route uploaded! Detected ${detected.length} airports within 20 NM: ${airportList}${moreText}`,
          { duration: 6000 }
        );
      } else {
        toast.success(`Route file "${file.name}" uploaded (no airports detected within 20 NM)`);
      }
    } catch (error) {
      toast.error("Failed to upload route file");
    } finally {
      setUploadingFile(false);
    }
  };

  // Load airports from a route file into FTX form
  const handleLoadAirportsFromRoute = (route) => {
    const airports = route.detectedAirports || [];
    if (airports.length === 0) {
      toast.error("No airports detected for this route. Try uploading the file again.");
      return;
    }
    
    setFtxForm(prev => ({
      ...prev,
      routeFile: route.name,
      airports: airports.map(a => ({
        icao: a.icao,
        name: a.name,
        lat: a.lat.toString(),
        lon: a.lon.toString()
      }))
    }));
    
    toast.success(`Loaded ${airports.length} airports from ${route.name}`);
  };

  // FTX Designator handlers
  const handleAddAirport = () => {
    setFtxForm(prev => ({
      ...prev,
      airports: [...prev.airports, { icao: "", name: "", lat: "", lon: "" }]
    }));
  };

  const handleRemoveAirport = (index) => {
    setFtxForm(prev => ({
      ...prev,
      airports: prev.airports.filter((_, i) => i !== index)
    }));
  };

  const handleAirportChange = (index, field, value) => {
    setFtxForm(prev => ({
      ...prev,
      airports: prev.airports.map((airport, i) => 
        i === index ? { ...airport, [field]: value } : airport
      )
    }));
  };

  const handleSaveFtx = async () => {
    if (!ftxForm.name.trim()) {
      toast.error("FTX Designator name is required");
      return;
    }

    // Validate airports have at least ICAO codes
    const validAirports = ftxForm.airports.filter(a => a.icao.trim());
    if (validAirports.length === 0) {
      toast.error("At least one airport with ICAO code is required");
      return;
    }

    // Build routeFiles array - handle both single routeFile and multiple routeFiles
    let routeFilesArray = ftxForm.routeFiles || [];
    // If routeFile is set but not in routeFiles, add it for backward compatibility
    if (ftxForm.routeFile && ftxForm.routeFile !== "none" && !routeFilesArray.includes(ftxForm.routeFile)) {
      routeFilesArray = [ftxForm.routeFile, ...routeFilesArray];
    }
    // Filter out "none" and empty values
    routeFilesArray = routeFilesArray.filter(rf => rf && rf !== "none");

    const ftxData = {
      name: ftxForm.name.trim(),
      routeFile: routeFilesArray.length > 0 ? routeFilesArray[0] : null,  // Backward compatibility
      routeFiles: routeFilesArray,  // Multiple route files
      description: ftxForm.description || "",
      loEmail: ftxForm.loEmail?.trim() || "",
      airports: validAirports.map(a => ({
        icao: a.icao.toUpperCase().trim(),
        name: a.name.trim() || a.icao.toUpperCase().trim(),
        lat: parseFloat(a.lat) || 0,
        lon: parseFloat(a.lon) || 0
      })),
      videoStreamUrl: ftxForm.videoStreamUrl?.trim() || "",
      excludeFromWeather: ftxForm.excludeFromWeather || false,
      excludeFromSkylegs: ftxForm.excludeFromSkylegs || false,
      isExternal: ftxForm.isExternal || false,
      adsbIcaoHex: ftxForm.adsbIcaoHex?.toLowerCase().trim() || ""
    };

    try {
      if (editingFtx !== null) {
        // Update existing
        await axios.put(`${API}/admin/ftx/${editingFtx}`, ftxData);
        setAdminData(prev => ({
          ...prev,
          ftxDesignators: prev.ftxDesignators.map((ftx, i) => 
            i === editingFtx ? ftxData : ftx
          )
        }));
        toast.success("FTX Designator updated");
      } else {
        // Add new
        await axios.post(`${API}/admin/config/ftxDesignators`, { item: ftxData });
        setAdminData(prev => ({
          ...prev,
          ftxDesignators: [...prev.ftxDesignators, ftxData]
        }));
        toast.success("FTX Designator created");
      }
      setShowFtxDialog(false);
      resetFtxForm();
    } catch (error) {
      toast.error("Failed to save FTX Designator");
    }
  };

  const handleEditFtx = (index) => {
    const ftx = adminData.ftxDesignators[index];
    // Handle both old string format and new object format
    if (typeof ftx === 'string') {
      setFtxForm({
        name: ftx,
        routeFile: "",
        routeFiles: [],
        description: "",
        loEmail: "",
        airports: [{ icao: "", name: "", lat: "", lon: "" }],
        videoStreamUrl: "",
        excludeFromWeather: false,
        excludeFromSkylegs: false,
        isExternal: false,
        adsbIcaoHex: ""
      });
    } else {
      // Build routeFiles array from both routeFile and routeFiles for backward compatibility
      let routeFilesArray = ftx.routeFiles || [];
      if (ftx.routeFile && !routeFilesArray.includes(ftx.routeFile)) {
        routeFilesArray = [ftx.routeFile, ...routeFilesArray];
      }
      
      setFtxForm({
        name: ftx.name || "",
        routeFile: routeFilesArray.length > 0 ? routeFilesArray[0] : "",
        routeFiles: routeFilesArray,
        description: ftx.description || "",
        loEmail: ftx.loEmail || "",
        airports: ftx.airports?.length > 0 
          ? ftx.airports.map(a => ({
              icao: a.icao || "",
              name: a.name || "",
              lat: a.lat?.toString() || "",
              lon: a.lon?.toString() || ""
            }))
          : [{ icao: "", name: "", lat: "", lon: "" }],
        videoStreamUrl: ftx.videoStreamUrl || "",
        excludeFromWeather: ftx.excludeFromWeather || false,
        excludeFromSkylegs: ftx.excludeFromSkylegs || false,
        isExternal: ftx.isExternal || false,
        adsbIcaoHex: ftx.adsbIcaoHex || ""
      });
    }
    setEditingFtx(index);
    setShowFtxDialog(true);
  };

  const resetFtxForm = () => {
    setFtxForm({
      name: "",
      routeFile: "",
      routeFiles: [],
      description: "",
      loEmail: "",
      airports: [{ icao: "", name: "", lat: "", lon: "" }],
      videoStreamUrl: "",
      excludeFromWeather: false,
      excludeFromSkylegs: false,
      isExternal: false,
      adsbIcaoHex: ""
    });
    setEditingFtx(null);
  };

  // ===========================================================================
  // [6] TAB DEFINITIONS
  // ===========================================================================

  const tabs = [
    { id: 'opsDashboard', label: 'System Health', icon: Monitor, color: 'text-red-600' },
    { id: 'fleetStatus', label: 'Fleet Status', icon: BarChart3, color: 'text-yellow-600' },
    { id: 'customerDownloads', label: 'Customer Downloads', icon: Share2, color: 'text-green-600' },
    { id: 'aircraftTracker', label: 'Aircraft Tracker', icon: Radar, color: 'text-orange-500' },
    { id: 'apiConfig', label: 'API Configuration', icon: Key, color: 'text-rose-600' },
    { id: 'ftxDesignators', label: 'FTX Designators', icon: MapPinned, color: 'text-orange-600' },
    { id: 'routes', label: 'Route Files', icon: Map, color: 'text-emerald-600' },
    { id: 'aircraft', label: 'Aircraft', icon: Plane, color: 'text-indigo-600' },
    { id: 'registrations', label: 'Registrations', icon: FileText, color: 'text-emerald-600' },
    { id: 'pilots', label: 'Pilots', icon: Users, color: 'text-blue-600' },
    { id: 'liaisonOfficers', label: 'Liaison Officers', icon: Users, color: 'text-purple-600' },
    { id: 'weatherDecisions', label: 'Weather Decisions', icon: Info, color: 'text-cyan-600' },
  ];

  // ===========================================================================
  // [7] RENDER - Password Gate
  // ===========================================================================

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
          <div className="text-center mb-6">
            <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Settings className="w-10 h-10 text-slate-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-800">Admin Settings</h1>
            <p className="text-sm text-slate-500 mt-2">Enter admin password to continue</p>
          </div>
          <div className="space-y-4">
            <Input
              type="password"
              placeholder="Admin password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
              className="text-center text-lg"
            />
            <Button onClick={handleLogin} className="w-full bg-slate-800 hover:bg-slate-900">
              Access Admin Panel
            </Button>
            <Button variant="ghost" onClick={() => navigate('/')} className="w-full">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen admin-page-container ${theme}`}>
      {/* Header */}
      <header className="admin-header sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="h-6 w-px bg-slate-200 dark:bg-slate-700" />
              <div className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                <h1 className="text-xl font-bold text-slate-800 dark:text-slate-200">Admin Settings</h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                className="admin-theme-toggle"
                onClick={toggleTheme}
                title={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
              >
                {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
              </button>
              <span className="text-sm text-slate-500 dark:text-slate-400">
                Manage application configurations
              </span>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex gap-8">
          {/* Sidebar */}
          <div className="w-64 flex-shrink-0">
            <nav className="space-y-1">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                    activeTab === tab.id
                      ? 'bg-slate-800 text-white'
                      : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
                  }`}
                >
                  <tab.icon className={`w-5 h-5 ${activeTab === tab.id ? 'text-white' : tab.color}`} />
                  <span className="font-medium">{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1 bg-white rounded-xl border border-slate-200 p-6 overflow-auto">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : (
              <>
                {/* =========================================================
                    [8] RENDER - SYSTEM HEALTH TAB
                    ========================================================= */}
                {activeTab === 'opsDashboard' && (
                  <div className="space-y-6">
                    {/* Header */}
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-lg font-bold text-slate-800">System Health & API Status</h2>
                        <p className="text-sm text-slate-500">
                          Monitor external APIs, cache status, and server connectivity
                          {healthLastRefresh && (
                            <span className="ml-2 text-xs text-slate-400">
                              Last check: {healthLastRefresh.toLocaleTimeString('en-GB')}
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={fetchSystemHealth} disabled={healthLoading} variant="outline">
                          <RefreshCw className={`w-4 h-4 mr-2 ${healthLoading ? 'animate-spin' : ''}`} />
                          Refresh Status
                        </Button>
                        <Button 
                          onClick={() => window.open('/ops', '_blank', 'noopener,noreferrer')}
                          className="bg-slate-900 hover:bg-black text-white"
                        >
                          <Monitor className="w-4 h-4 mr-2" />
                          Open Ops Dashboard
                        </Button>
                      </div>
                    </div>

                    {healthLoading && !systemHealth ? (
                      <div className="flex items-center justify-center py-20">
                        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                        <span className="ml-3 text-slate-500">Checking system health...</span>
                      </div>
                    ) : systemHealth ? (
                      <>
                        {/* Summary Cards */}
                        <div className="grid grid-cols-4 gap-4">
                          <div className="border-2 border-slate-200 rounded-lg p-4 text-center">
                            <p className="text-3xl font-bold text-slate-800">{systemHealth.summary?.total || 0}</p>
                            <p className="text-xs text-slate-500 uppercase font-semibold">Total Services</p>
                          </div>
                          <div className="border-2 border-emerald-200 rounded-lg p-4 text-center bg-emerald-50">
                            <p className="text-3xl font-bold text-emerald-600">{systemHealth.summary?.online || 0}</p>
                            <p className="text-xs text-emerald-600 uppercase font-semibold">Online</p>
                          </div>
                          <div className="border-2 border-amber-200 rounded-lg p-4 text-center bg-amber-50">
                            <p className="text-3xl font-bold text-amber-600">{systemHealth.summary?.degraded || 0}</p>
                            <p className="text-xs text-amber-600 uppercase font-semibold">Degraded</p>
                          </div>
                          <div className="border-2 border-red-200 rounded-lg p-4 text-center bg-red-50">
                            <p className="text-3xl font-bold text-red-600">{systemHealth.summary?.offline || 0}</p>
                            <p className="text-xs text-red-600 uppercase font-semibold">Offline</p>
                          </div>
                        </div>

                        {/* API Status Grid */}
                        <div className="border-2 border-slate-200 rounded-lg overflow-hidden">
                          <div className="bg-slate-100 px-4 py-3 border-b-2 border-slate-200">
                            <span className="font-bold text-slate-800 uppercase tracking-wide">External API Status</span>
                          </div>
                          <div className="p-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                              {Object.entries(systemHealth.services || {}).filter(([key]) => key !== 'unifi').map(([key, service]) => (
                                <div 
                                  key={key}
                                  className={`rounded-lg p-4 border-2 ${
                                    service.status === 'online' 
                                      ? 'bg-emerald-50 border-emerald-200' 
                                      : service.status === 'degraded'
                                      ? 'bg-amber-50 border-amber-200'
                                      : 'bg-red-50 border-red-200'
                                  }`}
                                >
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="font-bold text-slate-800">{service.name}</span>
                                    <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${
                                      service.status === 'online'
                                        ? 'bg-emerald-500 text-white'
                                        : service.status === 'degraded'
                                        ? 'bg-amber-500 text-white'
                                        : 'bg-red-500 text-white'
                                    }`}>
                                      {service.status}
                                    </span>
                                  </div>
                                  <p className="text-sm text-slate-600">{service.message}</p>
                                  {service.response_time_ms && (
                                    <p className="text-xs text-slate-400 mt-1">
                                      Response: {service.response_time_ms.toFixed(0)}ms
                                    </p>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Critical Network Units Ostend */}
                        {systemHealth.services?.unifi && (() => {
                          // Override Unifi status to online if WireGuard is online
                          const unifiEffectiveStatus = systemHealth.services?.wireguard?.status === 'online' 
                            ? 'online' 
                            : systemHealth.services.unifi.status;
                          
                          // Helper function to get device icon
                          const getDeviceIcon = (type) => {
                            switch(type?.toLowerCase()) {
                              case 'uxg':
                              case 'ugw':
                              case 'udm':
                                return <Router className="w-8 h-8 text-orange-400" />;
                              case 'usw':
                              case 'usw-leaf':
                                return <Network className="w-8 h-8 text-blue-400" />;
                              case 'uap':
                              case 'u7':
                                return <Radio className="w-8 h-8 text-green-400" />;
                              default:
                                return <Monitor className="w-8 h-8 text-slate-400" />;
                            }
                          };
                          
                          return (
                          <div className="border-2 border-cyan-600 dark:border-cyan-500 rounded-lg overflow-hidden bg-slate-800 dark:bg-slate-900">
                            <div className="bg-cyan-900 dark:bg-cyan-950 px-4 py-3 border-b-2 border-cyan-600 dark:border-cyan-500 flex justify-between items-center">
                              <div className="flex items-center gap-2">
                                <Wifi className="w-5 h-5 text-cyan-400" />
                                <span className="font-bold text-white uppercase tracking-wide">
                                  {systemHealth.services.unifi.details?.site_name || 'Critical Network Units Ostend'}
                                </span>
                              </div>
                              <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${
                                unifiEffectiveStatus === 'online'
                                  ? 'bg-emerald-500 text-white'
                                  : unifiEffectiveStatus === 'degraded'
                                  ? 'bg-amber-500 text-white'
                                  : 'bg-red-500 text-white'
                              }`}>
                                {unifiEffectiveStatus}
                              </span>
                            </div>
                            <div className="p-4">
                              {/* Network Stats Row */}
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                                <div className="bg-slate-700 dark:bg-slate-800 rounded-lg p-4 border border-slate-600 text-center">
                                  <Shield className="w-6 h-6 mx-auto mb-2 text-purple-400" />
                                  <p className="text-3xl font-bold text-white">
                                    {systemHealth.services.unifi.details?.devices_online || 0}
                                    <span className="text-lg text-slate-400">/{systemHealth.services.unifi.details?.devices_total || 0}</span>
                                  </p>
                                  <p className="text-xs text-slate-400 uppercase font-semibold">Devices Online</p>
                                </div>
                                <div className="bg-slate-700 dark:bg-slate-800 rounded-lg p-4 border border-slate-600 text-center">
                                  <Wifi className="w-6 h-6 mx-auto mb-2 text-cyan-400" />
                                  <p className="text-3xl font-bold text-white">
                                    {systemHealth.services.unifi.details?.clients_connected || 0}
                                  </p>
                                  <p className="text-xs text-slate-400 uppercase font-semibold">Connected Clients</p>
                                </div>
                                <div className="bg-slate-700 dark:bg-slate-800 rounded-lg p-4 border border-slate-600 text-center">
                                  <Clock className="w-6 h-6 mx-auto mb-2 text-slate-400" />
                                  <p className="text-2xl font-bold text-white">
                                    {systemHealth.services.unifi.response_time_ms?.toFixed(0) || '-'}
                                    <span className="text-sm text-slate-400">ms</span>
                                  </p>
                                  <p className="text-xs text-slate-400 uppercase font-semibold">Response Time</p>
                                </div>
                                <div className="bg-slate-700 dark:bg-slate-800 rounded-lg p-4 border border-slate-600 text-center">
                                  <Database className="w-6 h-6 mx-auto mb-2 text-cyan-400" />
                                  <p className="text-lg font-bold text-white">network.kbelektro.be</p>
                                  <p className="text-xs text-slate-400 uppercase font-semibold">Controller</p>
                                </div>
                              </div>

                              {/* Network Devices Grid */}
                              {systemHealth.services.unifi.details?.devices && systemHealth.services.unifi.details.devices.length > 0 && (
                                <div className="bg-slate-700 dark:bg-slate-800 rounded-lg p-4 border border-slate-600 mb-4">
                                  <p className="text-xs text-slate-400 uppercase font-semibold mb-3">Network Devices</p>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {systemHealth.services.unifi.details.devices.map((device, idx) => (
                                      <div 
                                        key={idx}
                                        className={`flex items-center gap-4 p-4 rounded-lg border-2 ${
                                          device.state === 'online'
                                            ? 'bg-slate-600/30 border-emerald-500/50'
                                            : 'bg-slate-800/50 border-red-500/50'
                                        }`}
                                      >
                                        {/* Device Icon */}
                                        <div className={`flex-shrink-0 p-2 rounded-lg ${
                                          device.state === 'online' ? 'bg-slate-700' : 'bg-slate-800'
                                        }`}>
                                          {getDeviceIcon(device.type)}
                                        </div>
                                        
                                        {/* Device Info */}
                                        <div className="flex-grow min-w-0">
                                          <div className="flex items-center gap-2 mb-1">
                                            <p className="font-bold text-white text-base truncate">{device.name}</p>
                                            <span className={`px-1.5 py-0.5 rounded text-xs font-bold uppercase ${
                                              device.state === 'online'
                                                ? 'bg-emerald-600 text-emerald-100'
                                                : 'bg-red-600 text-red-100'
                                            }`}>
                                              {device.state}
                                            </span>
                                          </div>
                                          <p className="text-xs text-slate-400 mb-2">{device.model} • {device.type.toUpperCase()}</p>
                                          
                                          {/* IP Address */}
                                          <span className="text-cyan-400 font-mono text-sm">{device.ip}</span>
                                        </div>
                                        
                                        {/* Uptime */}
                                        <div className="flex-shrink-0 text-right">
                                          <p className="text-xs text-slate-500 uppercase">Uptime</p>
                                          <p className={`font-bold text-lg ${
                                            device.state === 'online' ? 'text-emerald-400' : 'text-red-400'
                                          }`}>
                                            {device.uptime}
                                          </p>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Subsystems Status */}
                              {systemHealth.services.unifi.details?.subsystems && (
                                <div className="bg-slate-700 dark:bg-slate-800 rounded-lg p-4 border border-slate-600">
                                  <p className="text-xs text-slate-400 uppercase font-semibold mb-3">Network Subsystems</p>
                                  <div className="flex flex-wrap gap-2">
                                    {Object.entries(systemHealth.services.unifi.details.subsystems).map(([subsys, status]) => {
                                      // Override VPN status if WireGuard is online
                                      let effectiveStatus = status;
                                      if (subsys === 'vpn' && systemHealth.services?.wireguard?.status === 'online') {
                                        effectiveStatus = 'ok';
                                      }
                                      return (
                                        <div 
                                          key={subsys}
                                          className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
                                            effectiveStatus === 'ok' 
                                              ? 'bg-emerald-900/50 border-emerald-600' 
                                              : 'bg-amber-900/50 border-amber-600'
                                          }`}
                                        >
                                          {effectiveStatus === 'ok' ? (
                                            <CheckCircle className="w-4 h-4 text-emerald-400" />
                                          ) : (
                                            <AlertTriangle className="w-4 h-4 text-amber-400" />
                                          )}
                                          <span className={`font-semibold uppercase text-sm ${
                                            effectiveStatus === 'ok' ? 'text-emerald-300' : 'text-amber-300'
                                          }`}>
                                            {subsys}
                                          </span>
                                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                                            effectiveStatus === 'ok' 
                                              ? 'bg-emerald-600 text-emerald-100' 
                                              : 'bg-amber-600 text-amber-100'
                                          }`}>
                                            {subsys === 'vpn' && systemHealth.services?.wireguard?.status === 'online' ? 'READY' : effectiveStatus.toUpperCase()}
                                          </span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}

                              {/* Controller Info */}
                              <div className="mt-4 flex items-center justify-between text-xs text-slate-400">
                                <span>Unifi Controller: network.kbelektro.be:22001</span>
                                <span>{systemHealth.services.unifi.message}</span>
                              </div>
                            </div>
                          </div>
                        );
                        })()}

                        {/* SkyLegs Cache Status */}
                        <div className="border-2 border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                          <div className="bg-slate-100 dark:bg-slate-800 px-4 py-3 border-b-2 border-slate-200 dark:border-slate-700 flex justify-between items-center">
                            <span className="font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wide">SkyLegs Data Cache</span>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={refreshSkyLegsCache}
                                disabled={cacheLoading || healthLoading}
                                className={`px-3 py-1 text-xs font-semibold rounded transition-colors ${
                                  cacheLoading || healthLoading
                                    ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                                    : 'bg-blue-500 hover:bg-blue-600 text-white'
                                }`}
                              >
                                {cacheLoading ? 'Refreshing...' : 'Refresh Cache'}
                              </button>
                              <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${
                                systemHealth.cache?.skylegs?.status === 'fresh'
                                  ? 'bg-emerald-500 text-white'
                                  : systemHealth.cache?.skylegs?.status === 'stale'
                                  ? 'bg-amber-500 text-white'
                                  : 'bg-red-500 text-white'
                              }`}>
                                {systemHealth.cache?.skylegs?.auto_refreshed ? 'AUTO-REFRESHED' : systemHealth.cache?.skylegs?.status || 'Unknown'}
                              </span>
                            </div>
                          </div>
                          <div className="p-4">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3">
                                <p className="text-xs text-slate-500 dark:text-slate-400 uppercase font-semibold">Cached Flights</p>
                                <p className="text-2xl font-bold text-slate-800 dark:text-white">{systemHealth.cache?.skylegs?.flight_count || 0}</p>
                              </div>
                              <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3">
                                <p className="text-xs text-slate-500 dark:text-slate-400 uppercase font-semibold">Cache Age</p>
                                <p className="text-lg font-bold text-slate-800 dark:text-white">{systemHealth.cache?.skylegs?.cache_age_formatted || 'N/A'}</p>
                              </div>
                              <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3">
                                <p className="text-xs text-slate-500 dark:text-slate-400 uppercase font-semibold">Last Refresh</p>
                                <p className="text-sm font-mono text-slate-600 dark:text-slate-300">{systemHealth.cache?.skylegs?.last_refresh_formatted || 'Never'}</p>
                              </div>
                              <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3">
                                <p className="text-xs text-slate-500 dark:text-slate-400 uppercase font-semibold">Status</p>
                                <p className={`text-lg font-bold ${
                                  systemHealth.cache?.skylegs?.status === 'fresh' ? 'text-emerald-600 dark:text-emerald-400' :
                                  systemHealth.cache?.skylegs?.status === 'stale' ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'
                                }`}>
                                  {systemHealth.cache?.skylegs?.status === 'fresh' ? '✓ Fresh' :
                                   systemHealth.cache?.skylegs?.status === 'stale' ? '⚠ Stale' : '✗ Expired'}
                                </p>
                              </div>
                              <div className="col-span-2 md:col-span-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3">
                                <p className="text-xs text-blue-600 dark:text-blue-300">
                                  <strong>Smart Cache:</strong> Auto-refreshes when expired/empty during health check. 
                                  Manual refresh available above. Fresh = &lt;10min, Stale = &lt;30min, Expired = &gt;30min
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-10 text-slate-400">
                        <Monitor className="w-10 h-10 mx-auto mb-2 opacity-50" />
                        <p>Click "Refresh Status" to check system health</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Fleet Status Tab */}
                {activeTab === 'fleetStatus' && (
                  <div className="space-y-6">
                    {/* Header */}
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-lg font-bold text-slate-800">Fleet Configuration</h2>
                        <p className="text-sm text-slate-500">
                          Manage FRONTEX Surveillance Asset contracts and maintenance schedules
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={fetchFleetConfigs} disabled={fleetLoading} variant="outline">
                          <RefreshCw className={`w-4 h-4 mr-2 ${fleetLoading ? 'animate-spin' : ''}`} />
                          Refresh
                        </Button>
                        <Button 
                          onClick={() => { resetFleetConfigForm(); setShowFleetConfigDialog(true); }}
                          className="bg-yellow-600 hover:bg-yellow-700 text-white"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add Asset
                        </Button>
                        <Button 
                          onClick={() => window.open('/fleet', '_blank', 'noopener,noreferrer')}
                          variant="outline"
                        >
                          <BarChart3 className="w-4 h-4 mr-2" />
                          View Dashboard
                        </Button>
                      </div>
                    </div>

                    {/* Loading State */}
                    {fleetLoading && fleetConfigs.length === 0 && (
                      <div className="text-center py-12 text-slate-500">
                        <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3" />
                        <p>Loading fleet configurations...</p>
                      </div>
                    )}

                    {/* Asset Configuration Cards */}
                    <div className="space-y-4">
                      {fleetConfigs.map((config) => {
                        const isSparrow = config.callsign?.includes('SPARROW');
                        const themeColor = isSparrow ? 'blue' : 'emerald';
                        
                        return (
                          <div 
                            key={config.callsign}
                            className="border border-slate-200 rounded-lg overflow-hidden hover:border-slate-300 transition-colors"
                          >
                            {/* Card Header */}
                            <div className={`bg-${themeColor}-50 border-b border-${themeColor}-100 px-4 py-3 flex items-center justify-between`}>
                              <div className="flex items-center gap-3">
                                <Plane className={`w-5 h-5 text-${themeColor}-600`} />
                                <span className="font-bold text-slate-800">{config.callsign}</span>
                                <span className={`text-xs px-2 py-0.5 rounded bg-${themeColor}-100 text-${themeColor}-700`}>
                                  {config.registration}
                                </span>
                                <span className="text-xs text-slate-500">{config.aircraft_type}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => handleEditFleetConfig(config)}
                                >
                                  Edit
                                </Button>
                              </div>
                            </div>

                            {/* Card Body */}
                            <div className="p-4">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {/* General Info */}
                                <div className="space-y-2">
                                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">General</h4>
                                  <div className="space-y-1 text-sm">
                                    <div className="flex justify-between">
                                      <span className="text-slate-500">Base</span>
                                      <span className="font-mono font-medium">{config.base}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-slate-500">Deployment</span>
                                      <span className="font-medium text-slate-700">{config.deployment || '-'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-slate-500">Aircraft ID</span>
                                      <span className="font-mono text-slate-600">{config.aircraft_id || '-'}</span>
                                    </div>
                                    {config.mission_id && (
                                      <div className="flex justify-between">
                                        <span className="text-slate-500">Mission ID</span>
                                        <span className="font-mono text-slate-600">{config.mission_id}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {/* Contract Info */}
                                <div className="space-y-2">
                                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Contract</h4>
                                  <div className="space-y-1 text-sm">
                                    <div className="flex justify-between">
                                      <span className="text-slate-500">Start</span>
                                      <span className="font-medium">{config.contract?.start_date || '-'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-slate-500">End</span>
                                      <span className="font-medium">{config.contract?.end_date || '-'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-slate-500">Duration</span>
                                      <span className="font-medium">{config.contract?.contracted_days || 0} days</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-slate-500">Target Hours</span>
                                      <span className="font-bold text-blue-600">{config.contract?.contracted_hours || 0}h</span>
                                    </div>
                                  </div>
                                </div>

                                {/* Maintenance Info */}
                                <div className="space-y-2">
                                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Maintenance</h4>
                                  <div className="space-y-1 text-sm">
                                    <div className="flex justify-between">
                                      <span className="text-slate-500">Type</span>
                                      <span className="font-medium">{config.maintenance?.type || '-'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-slate-500">Last at</span>
                                      <span className="font-mono">{config.maintenance?.last_maintenance_hours || 0}h</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-slate-500">Next due at</span>
                                      <span className="font-mono font-bold text-orange-600">{config.maintenance?.next_maintenance_at || 0}h</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-slate-500">Duration</span>
                                      <span className="font-medium">{config.maintenance?.estimated_duration_days || 3} days</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Empty State */}
                    {!fleetLoading && fleetConfigs.length === 0 && (
                      <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-lg">
                        <Plane className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                        <h3 className="text-lg font-medium text-slate-600">No Fleet Configurations</h3>
                        <p className="text-sm text-slate-400 mt-1">Add your first surveillance asset configuration</p>
                        <Button 
                          onClick={() => { resetFleetConfigForm(); setShowFleetConfigDialog(true); }}
                          className="mt-4"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add Asset
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Customer Downloads Tab */}
                {activeTab === 'customerDownloads' && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-lg font-bold text-slate-800">Customer Downloads</h2>
                        <p className="text-sm text-slate-500">Create PIN codes for customers to download redacted videos</p>
                      </div>
                      <Button onClick={fetchCustomerDownloadsData} disabled={customerDownloadsLoading}>
                        <RefreshCw className={`w-4 h-4 mr-2 ${customerDownloadsLoading ? 'animate-spin' : ''}`} />
                        Refresh
                      </Button>
                    </div>

                    {/* Create New PIN Section */}
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200 p-6">
                      <h3 className="text-md font-semibold text-slate-800 mb-4 flex items-center gap-2">
                        <Share2 className="w-5 h-5 text-green-600" />
                        Create New Customer PIN
                      </h3>
                      
                      {/* Available Videos */}
                      <div className="mb-4">
                        <Label className="text-sm font-medium text-slate-700 mb-2 block">
                          Select Redacted Videos ({availableVideos.length} available)
                        </Label>
                        {availableVideos.length === 0 ? (
                          <p className="text-sm text-slate-500 italic">
                            No redacted videos available. Create redacted versions from the Video Library first.
                          </p>
                        ) : (
                          <div className="space-y-2 max-h-48 overflow-y-auto bg-white rounded-md border p-2">
                            {availableVideos.map((video, idx) => (
                              <label key={idx} className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={selectedVideos.includes(video.filename)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedVideos([...selectedVideos, video.filename]);
                                    } else {
                                      setSelectedVideos(selectedVideos.filter(v => v !== video.filename));
                                    }
                                  }}
                                  className="w-4 h-4 rounded border-slate-300"
                                />
                                <span className="flex-1 text-sm text-slate-700">{video.display_name}</span>
                                <span className="text-xs text-slate-500">{formatFileSize(video.size)}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      <Button 
                        onClick={handleCreatePin} 
                        disabled={creatingPin || selectedVideos.length === 0}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        {creatingPin ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</>
                        ) : (
                          <><Plus className="w-4 h-4 mr-2" /> Generate PIN</>
                        )}
                      </Button>
                      
                      {/* New PIN Result */}
                      {newPinResult && (
                        <div className="mt-4 p-4 bg-white rounded-lg border-2 border-green-400">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-slate-700">New PIN Created:</span>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => copyToClipboard(newPinResult.pin)}
                            >
                              <Copy className="w-4 h-4 mr-1" /> Copy
                            </Button>
                          </div>
                          <div className="text-3xl font-mono font-bold text-green-600 tracking-widest text-center py-2">
                            {newPinResult.pin}
                          </div>
                          <p className="text-xs text-slate-500 text-center mt-2">
                            Send this PIN to the customer via WhatsApp
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Active PINs List */}
                    <div className="bg-white rounded-lg border p-6">
                      <h3 className="text-md font-semibold text-slate-800 mb-4 flex items-center gap-2">
                        <FileText className="w-5 h-5 text-blue-600" />
                        Active & Recent PINs
                      </h3>
                      
                      {customerDownloadsLoading ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                        </div>
                      ) : customerPins.length === 0 ? (
                        <p className="text-sm text-slate-500 italic text-center py-8">
                          No PINs created yet
                        </p>
                      ) : (
                        <div className="space-y-3">
                          {customerPins.map((pin, idx) => (
                            <div 
                              key={idx} 
                              className={`p-4 rounded-lg border ${
                                pin.status === 'active' ? 'border-green-200 bg-green-50' :
                                pin.status === 'expired' ? 'border-amber-200 bg-amber-50' :
                                'border-red-200 bg-red-50'
                              }`}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-3">
                                  <span className="text-xl font-mono font-bold tracking-widest">
                                    {pin.pin}
                                  </span>
                                  <span className={`px-2 py-0.5 rounded text-xs font-semibold uppercase ${
                                    pin.status === 'active' ? 'bg-green-100 text-green-700' :
                                    pin.status === 'expired' ? 'bg-amber-100 text-amber-700' :
                                    'bg-red-100 text-red-700'
                                  }`}>
                                    {pin.status}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button 
                                    size="sm" 
                                    variant="ghost"
                                    onClick={() => copyToClipboard(pin.pin)}
                                  >
                                    <Copy className="w-4 h-4" />
                                  </Button>
                                  {pin.status === 'expired' && (
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      onClick={() => handleReinstatePin(pin.pin)}
                                      className="text-amber-600 border-amber-300"
                                    >
                                      <RotateCcw className="w-4 h-4 mr-1" /> Reinstate
                                    </Button>
                                  )}
                                  {pin.status === 'active' && (
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      onClick={() => handleRevokePin(pin.pin)}
                                      className="text-red-600 border-red-300"
                                    >
                                      <XCircle className="w-4 h-4 mr-1" /> Revoke
                                    </Button>
                                  )}
                                </div>
                              </div>
                              
                              <div className="flex flex-wrap gap-4 text-xs text-slate-600">
                                <span className="flex items-center gap-1">
                                  <Download className="w-3 h-3" />
                                  {pin.videos?.length || 0} video(s)
                                </span>
                                <span className="flex items-center gap-1">
                                  <Eye className="w-3 h-3" />
                                  {pin.access_count || 0} access(es)
                                </span>
                                {pin.expires_at && (
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {getTimeRemaining(pin.expires_at)}
                                  </span>
                                )}
                                {!pin.first_accessed_at && (
                                  <span className="text-slate-400 italic">Not accessed yet</span>
                                )}
                              </div>
                              
                              {pin.videos && pin.videos.length > 0 && (
                                <div className="mt-2 text-xs text-slate-500">
                                  {pin.videos.map(v => v.filename).join(', ')}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Download Portal Link */}
                    <div className="bg-slate-50 rounded-lg border p-4">
                      <p className="text-sm text-slate-600">
                        <strong>Customer Download URL:</strong>{' '}
                        <code className="bg-white px-2 py-1 rounded border text-xs">
                          {window.location.origin}/download
                        </code>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="ml-2"
                          onClick={() => copyToClipboard(`${window.location.origin}/download`)}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </p>
                    </div>
                  </div>
                )}

                {/* Aircraft Tracker Tab */}
                {activeTab === 'aircraftTracker' && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-lg font-bold text-slate-800">Aircraft Tracker</h2>
                        <p className="text-sm text-slate-500">Manage aircraft to track via ADS-B Exchange</p>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" onClick={fetchTrackedAircraft} disabled={trackedAircraftLoading}>
                          <RefreshCw className={`w-4 h-4 mr-2 ${trackedAircraftLoading ? 'animate-spin' : ''}`} />
                          Refresh
                        </Button>
                        <Button onClick={() => handleOpenAircraftDialog()}>
                          <Plus className="w-4 h-4 mr-2" />
                          Add Aircraft
                        </Button>
                      </div>
                    </div>

                    {/* Info Box */}
                    <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg border border-orange-200 p-4">
                      <div className="flex items-start gap-3">
                        <Info className="w-5 h-5 text-orange-600 mt-0.5" />
                        <div className="text-sm text-slate-700">
                          <p className="font-medium mb-1">How to find ICAO Hex codes:</p>
                          <p className="text-slate-600">
                            Visit <a href="https://globe.adsbexchange.com" target="_blank" rel="noopener noreferrer" className="text-orange-600 underline">globe.adsbexchange.com</a>, 
                            search for an aircraft, and the ICAO hex code will be shown in the URL (e.g., <code className="bg-white px-1 rounded">?icao=a3b771</code>).
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Aircraft List */}
                    <div className="space-y-3">
                      {trackedAircraftLoading ? (
                        <div className="flex items-center justify-center py-12">
                          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                        </div>
                      ) : trackedAircraft.length === 0 ? (
                        <div className="text-center py-12 text-slate-500">
                          <Radar className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                          <p>No aircraft configured for tracking</p>
                          <p className="text-sm">Click "Add Aircraft" to get started</p>
                        </div>
                      ) : (
                        trackedAircraft.map((aircraft) => (
                          <div 
                            key={aircraft.icao_hex} 
                            className={`p-4 rounded-lg border transition-colors ${
                              aircraft.active 
                                ? 'bg-white border-slate-200 hover:border-slate-300' 
                                : 'bg-slate-50 border-slate-200 opacity-60'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div 
                                  className="w-10 h-10 rounded-full flex items-center justify-center"
                                  style={{ backgroundColor: `${aircraft.color}20` }}
                                >
                                  <Plane className="w-5 h-5" style={{ color: aircraft.color }} />
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="font-bold text-slate-800">{aircraft.callsign}</span>
                                    <span className="text-slate-600">{aircraft.registration}</span>
                                    {aircraft.active ? (
                                      <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Active</span>
                                    ) : (
                                      <span className="px-2 py-0.5 bg-slate-200 text-slate-600 text-xs rounded-full">Inactive</span>
                                    )}
                                  </div>
                                  <div className="text-sm text-slate-500">
                                    ICAO: <code className="bg-slate-100 px-1 rounded">{aircraft.icao_hex}</code>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => handleToggleAircraftActive(aircraft)}
                                >
                                  {aircraft.active ? (
                                    <><XCircle className="w-4 h-4 mr-1" /> Deactivate</>
                                  ) : (
                                    <><CheckCircle className="w-4 h-4 mr-1" /> Activate</>
                                  )}
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => handleOpenAircraftDialog(aircraft)}
                                >
                                  <Edit2 className="w-4 h-4 mr-1" /> Edit
                                </Button>
                                <button
                                  onClick={() => handleDeleteAircraft(aircraft.icao_hex)}
                                  className="p-2 text-red-500 hover:bg-red-50 rounded transition-colors"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    {/* Quick Link */}
                    <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <p className="text-sm text-slate-600">
                        View live tracking at: <a href="/tracker" className="text-orange-600 underline font-medium">/tracker</a>
                      </p>
                    </div>
                  </div>
                )}

                {/* API Configuration Tab */}
                {activeTab === 'apiConfig' && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-lg font-bold text-slate-800">API Configuration</h2>
                        <p className="text-sm text-slate-500">Manage API keys and service credentials</p>
                      </div>
                      <Button variant="outline" onClick={fetchApiConfigs} disabled={apiConfigsLoading}>
                        <RefreshCw className={`w-4 h-4 mr-2 ${apiConfigsLoading ? 'animate-spin' : ''}`} />
                        Refresh
                      </Button>
                    </div>

                    {/* Warning Box */}
                    <div className="bg-gradient-to-r from-rose-50 to-red-50 rounded-lg border border-rose-200 p-4">
                      <div className="flex items-start gap-3">
                        <Shield className="w-5 h-5 text-rose-600 mt-0.5" />
                        <div className="text-sm text-slate-700">
                          <p className="font-medium mb-1">Security Notice</p>
                          <p className="text-slate-600">
                            API keys are sensitive credentials. Changes take effect immediately. Some keys may require a server restart for full effect.
                          </p>
                        </div>
                      </div>
                    </div>

                    {apiConfigsLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                      </div>
                    ) : (
                      <div className="space-y-6">
                        {Object.entries(apiCategories).map(([category, configs]) => {
                          const CategoryIcon = getCategoryIcon(category);
                          return (
                            <div key={category} className="space-y-3">
                              <div className="flex items-center gap-2 pb-2 border-b border-slate-200">
                                <CategoryIcon className="w-5 h-5 text-slate-600" />
                                <h3 className="font-semibold text-slate-800">{category}</h3>
                                <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                                  {configs.length} {configs.length === 1 ? 'key' : 'keys'}
                                </span>
                              </div>
                              
                              <div className="space-y-2">
                                {configs.map((config) => (
                                  <div 
                                    key={config.key}
                                    className={`p-4 rounded-lg border transition-colors ${
                                      config.is_set 
                                        ? 'bg-white border-slate-200 hover:border-slate-300' 
                                        : 'bg-red-50 border-red-200'
                                    }`}
                                  >
                                    <div className="flex items-start justify-between gap-4">
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                          <span className="font-medium text-slate-800">{config.name}</span>
                                          {config.required && (
                                            <span className="text-xs text-rose-600 bg-rose-100 px-1.5 py-0.5 rounded">Required</span>
                                          )}
                                          {config.is_secret && (
                                            <span className="text-xs text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">Secret</span>
                                          )}
                                          {config.is_set ? (
                                            <CheckCircle className="w-4 h-4 text-green-600" />
                                          ) : (
                                            <XCircle className="w-4 h-4 text-red-500" />
                                          )}
                                        </div>
                                        <p className="text-sm text-slate-500 mb-2">{config.description}</p>
                                        
                                        {editingApiKey === config.key ? (
                                          <div className="flex items-center gap-2 mt-2">
                                            <Input
                                              type={config.is_secret ? "password" : "text"}
                                              value={newApiValue}
                                              onChange={(e) => setNewApiValue(e.target.value)}
                                              placeholder={`Enter ${config.name}`}
                                              className="flex-1 font-mono text-sm"
                                            />
                                            <Button 
                                              size="sm" 
                                              onClick={() => handleUpdateApiConfig(config.key)}
                                              className="bg-green-600 hover:bg-green-700"
                                            >
                                              Save
                                            </Button>
                                            <Button 
                                              size="sm" 
                                              variant="outline"
                                              onClick={() => { setEditingApiKey(null); setNewApiValue(""); }}
                                            >
                                              Cancel
                                            </Button>
                                          </div>
                                        ) : (
                                          <div className="flex items-center gap-2">
                                            <code className="text-sm bg-slate-100 px-2 py-1 rounded font-mono text-slate-600">
                                              {config.is_set ? config.display_value : 'Not set'}
                                            </code>
                                            <span className="text-xs text-slate-400">
                                              ({config.source === 'database' ? 'custom' : config.source})
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                      
                                      <div className="flex items-center gap-1">
                                        {config.docs_url && (
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => window.open(config.docs_url, '_blank')}
                                            title="Open documentation"
                                          >
                                            <ExternalLink className="w-4 h-4 text-slate-400" />
                                          </Button>
                                        )}
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => handleTestApiConfig(config.key)}
                                          disabled={!config.is_set || testingApiKey === config.key}
                                          title="Test API key"
                                        >
                                          {testingApiKey === config.key ? (
                                            <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                                          ) : (
                                            <TestTube className="w-4 h-4 text-blue-500" />
                                          )}
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => { setEditingApiKey(config.key); setNewApiValue(""); }}
                                          title="Edit"
                                        >
                                          <Edit2 className="w-4 h-4 text-slate-400" />
                                        </Button>
                                        {config.source === 'database' && (
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleResetApiConfig(config.key)}
                                            title="Reset to default"
                                          >
                                            <RotateCcw className="w-4 h-4 text-slate-400" />
                                          </Button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Info Box */}
                    <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <p className="text-sm text-slate-600">
                        <strong>Note:</strong> Changes to API keys are stored in the database and take effect immediately.
                        Environment variables in <code className="bg-white px-1 rounded">.env</code> serve as defaults.
                      </p>
                    </div>
                  </div>
                )}

                {/* FTX Designators Tab */}
                {activeTab === 'ftxDesignators' && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-lg font-bold text-slate-800">FTX Designators</h2>
                        <p className="text-sm text-slate-500">Manage flight exercise designators with linked routes and airports</p>
                      </div>
                      <Button onClick={() => { resetFtxForm(); setShowFtxDialog(true); }}>
                        <Plus className="w-4 h-4 mr-2" />
                        Add FTX Designator
                      </Button>
                    </div>

                    <div className="space-y-3">
                      {adminData.ftxDesignators?.length > 0 ? (
                        adminData.ftxDesignators.map((ftx, index) => {
                          const ftxName = typeof ftx === 'string' ? ftx : ftx.name;
                          const ftxAirports = typeof ftx === 'object' ? ftx.airports : [];
                          const ftxRoute = typeof ftx === 'object' ? ftx.routeFile : null;
                          const ftxLoEmail = typeof ftx === 'object' ? ftx.loEmail : null;
                          const isExternal = typeof ftx === 'object' ? ftx.isExternal : false;
                          const excludeFromWeather = typeof ftx === 'object' ? ftx.excludeFromWeather : false;
                          const excludeFromSkylegs = typeof ftx === 'object' ? ftx.excludeFromSkylegs : false;
                          const videoStreamUrl = typeof ftx === 'object' ? ftx.videoStreamUrl : null;
                          
                          return (
                            <div 
                              key={index} 
                              className={`p-4 rounded-lg border transition-colors ${
                                isExternal 
                                  ? 'bg-amber-50 border-amber-200 hover:border-amber-300' 
                                  : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                              }`}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                                    <MapPinned className={`w-5 h-5 ${isExternal ? 'text-amber-600' : 'text-orange-600'}`} />
                                    <span className="font-bold text-slate-800">{ftxName}</span>
                                    {isExternal && (
                                      <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full flex items-center gap-1">
                                        <Users className="w-3 h-3" />
                                        External
                                      </span>
                                    )}
                                    {ftxRoute && (
                                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full flex items-center gap-1">
                                        <Link className="w-3 h-3" />
                                        Route linked
                                      </span>
                                    )}
                                    {videoStreamUrl && (
                                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full flex items-center gap-1">
                                        <Monitor className="w-3 h-3" />
                                        Video
                                      </span>
                                    )}
                                    {excludeFromWeather && (
                                      <span className="px-2 py-0.5 bg-slate-200 text-slate-600 text-xs rounded-full flex items-center gap-1">
                                        <Cloud className="w-3 h-3" />
                                        No weather
                                      </span>
                                    )}
                                    {excludeFromSkylegs && (
                                      <span className="px-2 py-0.5 bg-slate-200 text-slate-600 text-xs rounded-full flex items-center gap-1">
                                        <XCircle className="w-3 h-3" />
                                        No SkyLegs
                                      </span>
                                    )}
                                  </div>
                                  {ftxLoEmail && (
                                    <div className="ml-8 mb-2 text-sm text-slate-600">
                                      <span className="font-medium">LO Email:</span> {ftxLoEmail}
                                    </div>
                                  )}
                                  {ftxAirports?.length > 0 && (
                                    <div className="ml-8 flex flex-wrap gap-2">
                                      {ftxAirports.map((airport, i) => (
                                        <span key={i} className="px-2 py-1 bg-white border border-slate-200 text-slate-600 text-xs rounded flex items-center gap-1">
                                          <Navigation className="w-3 h-3" />
                                          {airport.icao} {airport.name && `- ${airport.name}`}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button variant="outline" size="sm" onClick={() => handleEditFtx(index)}>
                                    Edit
                                  </Button>
                                  <button
                                    onClick={() => handleDeleteItem('ftxDesignators', index)}
                                    className="p-2 text-red-500 hover:bg-red-50 rounded"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="text-center py-12 text-slate-400">
                          <MapPinned className="w-12 h-12 mx-auto mb-3 opacity-50" />
                          <p>No FTX Designators configured</p>
                          <p className="text-sm">Click &quot;Add FTX Designator&quot; to create one</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Routes Tab */}
                {activeTab === 'routes' && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-lg font-bold text-slate-800">Route Files</h2>
                      <p className="text-sm text-slate-500">Upload KML/KMZ files to define flight routes</p>
                    </div>

                    <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
                      <input
                        type="file"
                        accept=".kml,.kmz"
                        onChange={handleFileUpload}
                        className="hidden"
                        id="route-upload"
                        disabled={uploadingFile}
                      />
                      <label htmlFor="route-upload" className="cursor-pointer flex flex-col items-center">
                        {uploadingFile ? (
                          <Loader2 className="w-12 h-12 text-slate-400 animate-spin mb-3" />
                        ) : (
                          <Upload className="w-12 h-12 text-slate-400 mb-3" />
                        )}
                        <span className="text-base font-medium text-slate-700">
                          {uploadingFile ? 'Analyzing route and detecting airports...' : 'Click to upload KML/KMZ file'}
                        </span>
                        <span className="text-sm text-slate-500 mt-1">
                          Airports within 20 NM will be automatically detected
                        </span>
                      </label>
                    </div>
                    
                    <div className="space-y-3">
                      <p className="text-sm font-medium text-slate-500 uppercase">Uploaded Routes</p>
                      {adminData.routes?.length > 0 ? (
                        adminData.routes.map((route, index) => {
                          const detectedAirports = route.detectedAirports || [];
                          return (
                            <div key={index} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-3">
                                  <Map className="w-5 h-5 text-emerald-600" />
                                  <div>
                                    <span className="font-medium text-slate-700">{route.name || route}</span>
                                    {route.uploadedAt && (
                                      <span className="text-xs text-slate-400 ml-2">
                                        {new Date(route.uploadedAt).toLocaleDateString()}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {detectedAirports.length > 0 && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => {
                                        handleLoadAirportsFromRoute(route);
                                        setShowFtxDialog(true);
                                      }}
                                      className="text-xs"
                                    >
                                      <Plus className="w-3 h-3 mr-1" />
                                      Create FTX with airports
                                    </Button>
                                  )}
                                  <button
                                    onClick={() => handleDeleteItem('routes', index)}
                                    className="p-2 text-red-500 hover:bg-red-50 rounded"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                              
                              {/* Show detected airports */}
                              {detectedAirports.length > 0 ? (
                                <div className="mt-3 pt-3 border-t border-slate-200">
                                  <p className="text-xs font-medium text-slate-500 mb-2 flex items-center gap-1">
                                    <Navigation className="w-3 h-3" />
                                    {detectedAirports.length} airports within 20 NM:
                                  </p>
                                  <div className="flex flex-wrap gap-2">
                                    {detectedAirports.map((airport, i) => (
                                      <span 
                                        key={i} 
                                        className="px-2 py-1 bg-white border border-slate-200 text-slate-600 text-xs rounded"
                                        title={`${airport.name} - ${airport.distance_nm} NM from route`}
                                      >
                                        {airport.icao} ({airport.distance_nm} NM)
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              ) : (
                                <p className="mt-2 text-xs text-slate-400">
                                  No airports detected within 20 NM of this route
                                </p>
                              )}
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-sm text-slate-400 text-center py-8">No routes uploaded yet</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Other Tabs - List Management */}
                {['aircraft', 'registrations', 'pilots', 'liaisonOfficers'].includes(activeTab) && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-lg font-bold text-slate-800 capitalize">
                        {activeTab === 'liaisonOfficers' ? 'Liaison Officers' : activeTab}
                      </h2>
                      <p className="text-sm text-slate-500">
                        {activeTab === 'aircraft' && 'Manage aircraft types available in briefing forms'}
                        {activeTab === 'registrations' && 'Manage aircraft registrations with associated types'}
                        {activeTab === 'pilots' && 'Manage the list of pilots in command'}
                        {activeTab === 'liaisonOfficers' && 'Manage liaison officers for missions'}
                      </p>
                    </div>

                    {/* Add New Item */}
                    <div className="flex gap-3">
                      <Input
                        placeholder={`Add new ${activeTab === 'liaisonOfficers' ? 'liaison officer' : activeTab.slice(0, -1)}`}
                        value={newItem}
                        onChange={(e) => setNewItem(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAddItem(activeTab)}
                        className="flex-1"
                      />
                      {activeTab === 'registrations' && (
                        <Select value={newItemExtra} onValueChange={setNewItemExtra}>
                          <SelectTrigger className="w-48">
                            <SelectValue placeholder="Aircraft Type" />
                          </SelectTrigger>
                          <SelectContent>
                            {adminData.aircraft?.map((ac, i) => (
                              <SelectItem key={i} value={ac}>{ac}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      <Button onClick={() => handleAddItem(activeTab)}>
                        <Plus className="w-4 h-4 mr-2" />
                        Add
                      </Button>
                    </div>

                    {/* Items List */}
                    <div className="space-y-2 max-h-[500px] overflow-y-auto">
                      {adminData[activeTab]?.length > 0 ? (
                        adminData[activeTab].map((item, index) => {
                          const TabIcon = tabs.find(t => t.id === activeTab)?.icon || FileText;
                          const iconColor = tabs.find(t => t.id === activeTab)?.color || 'text-slate-600';
                          
                          return (
                            <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                              <div className="flex items-center gap-3">
                                <TabIcon className={`w-5 h-5 ${iconColor}`} />
                                <span className="font-medium text-slate-700">
                                  {typeof item === 'object' ? `${item.name} (${item.aircraftType})` : item}
                                </span>
                              </div>
                              <button
                                onClick={() => handleDeleteItem(activeTab, index)}
                                className="p-2 text-red-500 hover:bg-red-50 rounded"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-sm text-slate-400 text-center py-12">No items yet</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Weather Decisions Info Tab */}
                {activeTab === 'weatherDecisions' && (
                  <div className="space-y-6">
                    {/* Weather Model Switch Panel */}
                    <div className="border-2 border-cyan-500 rounded-lg overflow-hidden bg-gradient-to-br from-slate-800 to-slate-900">
                      <div className="bg-cyan-900 px-4 py-3 border-b-2 border-cyan-500 flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <Zap className="w-5 h-5 text-cyan-400" />
                          <span className="font-bold text-white uppercase tracking-wide">Weather Decision Model</span>
                        </div>
                        {weatherModel && (
                          <span className="px-3 py-1 bg-cyan-600 text-white text-xs font-bold rounded uppercase">
                            Active: {weatherModel.model_details?.name || weatherModel.active_model}
                          </span>
                        )}
                      </div>
                      <div className="p-4">
                        {weatherModelLoading ? (
                          <div className="text-center py-4">
                            <RefreshCw className="w-6 h-6 animate-spin mx-auto text-cyan-400" />
                            <p className="text-slate-400 text-sm mt-2">Loading models...</p>
                          </div>
                        ) : weatherModel ? (
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {Object.entries(weatherModel.available_models || {}).map(([modelId, model]) => (
                              <button
                                key={modelId}
                                onClick={() => switchWeatherModel(modelId)}
                                disabled={weatherModelLoading}
                                className={`p-4 rounded-lg border-2 text-left transition-all ${
                                  weatherModel.active_model === modelId
                                    ? 'border-cyan-400 bg-cyan-900/50 ring-2 ring-cyan-400/50'
                                    : 'border-slate-600 bg-slate-700/50 hover:border-slate-500 hover:bg-slate-700'
                                }`}
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <span className="font-bold text-white">{model.name}</span>
                                  {weatherModel.active_model === modelId && (
                                    <CheckCircle className="w-5 h-5 text-cyan-400" />
                                  )}
                                </div>
                                <p className="text-xs text-slate-400 mb-3">{model.description}</p>
                                <div className="space-y-1">
                                  {model.features?.map((feature, idx) => (
                                    <div key={idx} className="flex items-center gap-2 text-xs text-slate-300">
                                      <span className="w-1 h-1 bg-cyan-400 rounded-full"></span>
                                      {feature}
                                    </div>
                                  ))}
                                </div>
                              </button>
                            ))}
                          </div>
                        ) : (
                          <p className="text-slate-400 text-center py-4">Could not load weather models</p>
                        )}
                        <p className="text-xs text-slate-500 mt-4 text-center">
                          Switch between decision models to change how weather briefings calculate GO/NO GO recommendations.
                          Changes take effect immediately for new briefings.
                        </p>
                      </div>
                    </div>

                    <div>
                      <h2 className="text-lg font-bold text-slate-800 dark:text-slate-200">AI Weather Decision Criteria</h2>
                      <p className="text-sm text-slate-500">
                        How the AI determines GO / NO GO / GO WITH CAUTION decisions for NSAS missions
                      </p>
                      <p className="text-xs text-cyan-600 mt-1">
                        ℹ️ Values are defined in <code className="bg-slate-100 px-1 rounded">config/weatherDecisionCriteria.js</code> - changes there auto-update this page and FlightRouteWeather
                      </p>
                    </div>

                    {/* Decision Flow Overview */}
                    <div className="p-4 bg-gradient-to-r from-cyan-50 to-blue-50 border border-cyan-200 rounded-lg">
                      <h3 className="font-semibold text-cyan-800 mb-3 flex items-center gap-2">
                        <Info className="w-5 h-5" />
                        Decision Flow Overview
                      </h3>
                      <div className="flex items-center gap-2 flex-wrap text-sm">
                        <span className="px-3 py-1 bg-white rounded-full border border-slate-200">TAF Analysis</span>
                        <span className="text-slate-400">→</span>
                        <span className="px-3 py-1 bg-white rounded-full border border-slate-200">METAR Check*</span>
                        <span className="text-slate-400">→</span>
                        <span className="px-3 py-1 bg-white rounded-full border border-slate-200">Flight Limitations</span>
                        <span className="text-slate-400">→</span>
                        <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full border border-emerald-200 font-medium">Decision</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-2">* METAR only affects GO/NO GO when briefing is &lt;{WEATHER_CRITERIA.metar.validityWindow} minutes before mission start</p>
                    </div>

                    {/* Aircraft Limitations - NO GO Criteria */}
                    <div className="border border-red-200 rounded-lg overflow-hidden">
                      <div className="bg-red-50 px-4 py-3 border-b border-red-200">
                        <h3 className="font-semibold text-red-800 flex items-center gap-2">
                          <XCircle className="w-5 h-5" />
                          NO GO Criteria (Immediate Rejection)
                        </h3>
                      </div>
                      <div className="p-4 space-y-4">
                        {DECISION_CRITERIA.noGo.map((criterion) => {
                          const IconComponent = { Cloud, Zap, Wind, Eye, Thermometer }[criterion.icon] || Cloud;
                          return (
                            <div key={criterion.id} className="flex items-start gap-3 p-3 bg-red-50/50 rounded-lg">
                              <IconComponent className="w-5 h-5 text-red-600 mt-0.5" />
                              <div>
                                <h4 className="font-medium text-slate-800">{criterion.name}</h4>
                                <p className="text-sm text-slate-600">{criterion.description}</p>
                                <p className="text-xs text-red-600 mt-1">→ {criterion.reason}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* GO WITH CAUTION Criteria */}
                    <div className="border border-amber-200 rounded-lg overflow-hidden">
                      <div className="bg-amber-50 px-4 py-3 border-b border-amber-200">
                        <h3 className="font-semibold text-amber-800 flex items-center gap-2">
                          <AlertTriangle className="w-5 h-5" />
                          GO WITH CAUTION Criteria
                        </h3>
                      </div>
                      <div className="p-4 space-y-4">
                        {DECISION_CRITERIA.caution.map((criterion) => {
                          const IconComponent = { Eye, Cloud, Thermometer, CloudRain }[criterion.icon] || Cloud;
                          return (
                            <div key={criterion.id} className="flex items-start gap-3 p-3 bg-amber-50/50 rounded-lg">
                              <IconComponent className="w-5 h-5 text-amber-600 mt-0.5" />
                              <div>
                                <h4 className="font-medium text-slate-800">{criterion.name}</h4>
                                <p className="text-sm text-slate-600">{criterion.description}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* GO Criteria */}
                    <div className="border border-emerald-200 rounded-lg overflow-hidden">
                      <div className="bg-emerald-50 px-4 py-3 border-b border-emerald-200">
                        <h3 className="font-semibold text-emerald-800 flex items-center gap-2">
                          <CheckCircle className="w-5 h-5" />
                          GO Criteria
                        </h3>
                      </div>
                      <div className="p-4">
                        <p className="text-sm text-slate-600">Mission is GO when ALL of the following are true:</p>
                        <ul className="mt-3 space-y-2 text-sm">
                          {DECISION_CRITERIA.go.map((criterion) => (
                            <li key={criterion.id} className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-emerald-500" />
                              <span>{criterion.name}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {/* Patrol Effectiveness */}
                    <div className="border border-slate-200 rounded-lg overflow-hidden">
                      <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                        <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                          <Eye className="w-5 h-5" />
                          Patrol Effectiveness Assessment
                        </h3>
                      </div>
                      <div className="p-4 space-y-3">
                        <div className="grid grid-cols-3 gap-3">
                          {Object.entries(PATROL_EFFECTIVENESS).map(([key, value]) => (
                            <div key={key} className={`p-3 bg-${value.color}-50 rounded-lg text-center border border-${value.color}-200`}>
                              <div className={`font-bold text-${value.color}-700`}>{value.label}</div>
                              <p className="text-xs text-slate-600 mt-1">{value.description}</p>
                            </div>
                          ))}
                        </div>
                        <p className="text-xs text-slate-500">Patrol effectiveness considers camera sensor requirements: visibility, cloud cover, and lighting conditions.</p>
                      </div>
                    </div>

                    {/* Technical Parameters */}
                    <div className="border border-slate-200 rounded-lg overflow-hidden">
                      <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                        <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                          <Settings className="w-5 h-5" />
                          Technical Parameters ({WEATHER_CRITERIA.aircraft.type})
                        </h3>
                      </div>
                      <div className="p-4">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div className="space-y-2">
                            {TECHNICAL_PARAMS.slice(0, 3).map((param, idx) => (
                              <div key={idx} className="flex justify-between p-2 bg-slate-50 rounded">
                                <span className="text-slate-600">{param.label}</span>
                                <span className="font-mono font-medium">{param.value}</span>
                              </div>
                            ))}
                          </div>
                          <div className="space-y-2">
                            {TECHNICAL_PARAMS.slice(3).map((param, idx) => (
                              <div key={idx} className="flex justify-between p-2 bg-slate-50 rounded">
                                <span className="text-slate-600">{param.label}</span>
                                <span className="font-mono font-medium">{param.value}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Data Sources */}
                    <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <h3 className="font-semibold text-slate-700 mb-2">Data Sources</h3>
                      <ul className="text-sm text-slate-600 space-y-1">
                        {DATA_SOURCES.map((source, idx) => (
                          <li key={idx}>• <strong>{source.name}:</strong> {source.source}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Fleet Configuration Dialog */}
      <Dialog open={showFleetConfigDialog} onOpenChange={(open) => { if (!open) { resetFleetConfigForm(); } setShowFleetConfigDialog(open); }}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plane className="w-5 h-5 text-yellow-600" />
              {editingFleetConfig ? `Edit ${editingFleetConfig}` : 'New Fleet Asset'}
            </DialogTitle>
            <DialogDescription>
              Configure surveillance asset contract and maintenance parameters for fleet tracking.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Basic Info Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-slate-700 border-b border-slate-200 pb-2">Basic Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="fleet-callsign">Callsign *</Label>
                  <Input
                    id="fleet-callsign"
                    placeholder="e.g., SPARROW3, OSPREY4"
                    value={fleetConfigForm.callsign}
                    onChange={(e) => setFleetConfigForm(prev => ({ ...prev, callsign: e.target.value }))}
                    disabled={!!editingFleetConfig}
                    className={editingFleetConfig ? 'bg-slate-100' : ''}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fleet-registration">Registration *</Label>
                  <Input
                    id="fleet-registration"
                    placeholder="e.g., OO-MSA"
                    value={fleetConfigForm.registration}
                    onChange={(e) => setFleetConfigForm(prev => ({ ...prev, registration: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fleet-aircraft-type">Aircraft Type</Label>
                  <Input
                    id="fleet-aircraft-type"
                    placeholder="e.g., DA62-MPP"
                    value={fleetConfigForm.aircraft_type}
                    onChange={(e) => setFleetConfigForm(prev => ({ ...prev, aircraft_type: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fleet-base">Base (ICAO)</Label>
                  <Input
                    id="fleet-base"
                    placeholder="e.g., EBOS, LEMG"
                    value={fleetConfigForm.base}
                    onChange={(e) => setFleetConfigForm(prev => ({ ...prev, base: e.target.value.toUpperCase() }))}
                    maxLength={4}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fleet-deployment">Deployment</Label>
                  <Input
                    id="fleet-deployment"
                    placeholder="e.g., JO France + Belgium"
                    value={fleetConfigForm.deployment}
                    onChange={(e) => setFleetConfigForm(prev => ({ ...prev, deployment: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fleet-aircraft-id">SkyLegs Aircraft ID</Label>
                  <Input
                    id="fleet-aircraft-id"
                    type="number"
                    placeholder="e.g., 16708"
                    value={fleetConfigForm.aircraft_id}
                    onChange={(e) => setFleetConfigForm(prev => ({ ...prev, aircraft_id: e.target.value }))}
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="fleet-mission-id">SkyLegs Mission ID (optional)</Label>
                  <Input
                    id="fleet-mission-id"
                    type="number"
                    placeholder="e.g., 256984 (for filtering flights)"
                    value={fleetConfigForm.mission_id}
                    onChange={(e) => setFleetConfigForm(prev => ({ ...prev, mission_id: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            {/* Contract Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-slate-700 border-b border-slate-200 pb-2">Contract Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="fleet-contract-start">Start Date</Label>
                  <Input
                    id="fleet-contract-start"
                    type="date"
                    value={fleetConfigForm.contract.start_date}
                    onChange={(e) => setFleetConfigForm(prev => ({ 
                      ...prev, 
                      contract: { ...prev.contract, start_date: e.target.value }
                    }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fleet-contract-end">End Date</Label>
                  <Input
                    id="fleet-contract-end"
                    type="date"
                    value={fleetConfigForm.contract.end_date}
                    onChange={(e) => setFleetConfigForm(prev => ({ 
                      ...prev, 
                      contract: { ...prev.contract, end_date: e.target.value }
                    }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fleet-contract-days">Contracted Days</Label>
                  <Input
                    id="fleet-contract-days"
                    type="number"
                    placeholder="e.g., 209"
                    value={fleetConfigForm.contract.contracted_days}
                    onChange={(e) => setFleetConfigForm(prev => ({ 
                      ...prev, 
                      contract: { ...prev.contract, contracted_days: e.target.value }
                    }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fleet-contract-hours">Contracted Hours *</Label>
                  <Input
                    id="fleet-contract-hours"
                    type="number"
                    step="0.1"
                    placeholder="e.g., 443.0"
                    value={fleetConfigForm.contract.contracted_hours}
                    onChange={(e) => setFleetConfigForm(prev => ({ 
                      ...prev, 
                      contract: { ...prev.contract, contracted_hours: e.target.value }
                    }))}
                  />
                  <p className="text-xs text-slate-500">Total flight hours target for this contract</p>
                </div>
              </div>
            </div>

            {/* Maintenance Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-slate-700 border-b border-slate-200 pb-2">Maintenance Schedule</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="fleet-maint-type">Maintenance Type</Label>
                  <Input
                    id="fleet-maint-type"
                    placeholder="e.g., 100h + 300h eng., 200h inspection"
                    value={fleetConfigForm.maintenance.type}
                    onChange={(e) => setFleetConfigForm(prev => ({ 
                      ...prev, 
                      maintenance: { ...prev.maintenance, type: e.target.value }
                    }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fleet-maint-last">Last Maintenance (hours)</Label>
                  <Input
                    id="fleet-maint-last"
                    type="number"
                    step="0.1"
                    placeholder="e.g., 0"
                    value={fleetConfigForm.maintenance.last_maintenance_hours}
                    onChange={(e) => setFleetConfigForm(prev => ({ 
                      ...prev, 
                      maintenance: { ...prev.maintenance, last_maintenance_hours: e.target.value }
                    }))}
                  />
                  <p className="text-xs text-slate-500">Hours at last maintenance (relative to contract start)</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fleet-maint-next">Next Maintenance Due (hours) *</Label>
                  <Input
                    id="fleet-maint-next"
                    type="number"
                    step="0.1"
                    placeholder="e.g., 300"
                    value={fleetConfigForm.maintenance.next_maintenance_at}
                    onChange={(e) => setFleetConfigForm(prev => ({ 
                      ...prev, 
                      maintenance: { ...prev.maintenance, next_maintenance_at: e.target.value }
                    }))}
                  />
                  <p className="text-xs text-slate-500">Next check due at this hour count</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fleet-maint-margin">Warning Margin (hours)</Label>
                  <Input
                    id="fleet-maint-margin"
                    type="number"
                    placeholder="e.g., 10"
                    value={fleetConfigForm.maintenance.margin}
                    onChange={(e) => setFleetConfigForm(prev => ({ 
                      ...prev, 
                      maintenance: { ...prev.maintenance, margin: e.target.value }
                    }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fleet-maint-duration">Est. Duration (days)</Label>
                  <Input
                    id="fleet-maint-duration"
                    type="number"
                    placeholder="e.g., 3"
                    value={fleetConfigForm.maintenance.estimated_duration_days}
                    onChange={(e) => setFleetConfigForm(prev => ({ 
                      ...prev, 
                      maintenance: { ...prev.maintenance, estimated_duration_days: e.target.value }
                    }))}
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFleetConfigDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveFleetConfig} 
              disabled={savingFleetConfig}
              className="bg-yellow-600 hover:bg-yellow-700"
            >
              {savingFleetConfig ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                editingFleetConfig ? 'Update Configuration' : 'Create Configuration'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* FTX Designator Dialog */}
      <Dialog open={showFtxDialog} onOpenChange={(open) => { if (!open) { resetFtxForm(); } setShowFtxDialog(open); }}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPinned className="w-5 h-5 text-orange-600" />
              {editingFtx !== null ? 'Edit FTX Designator' : 'New FTX Designator'}
            </DialogTitle>
            <DialogDescription>
              Configure the FTX designator with linked route file and airports for weather predictions.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="ftx-name">Designator Name *</Label>
              <Input
                id="ftx-name"
                placeholder="e.g., SPARROW 4, EAGLE 5"
                value={ftxForm.name}
                onChange={(e) => setFtxForm(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="ftx-desc">Description</Label>
              <Input
                id="ftx-desc"
                placeholder="e.g., Flight Line JO France - 120 NM"
                value={ftxForm.description}
                onChange={(e) => setFtxForm(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>

            {/* LO Email */}
            <div className="space-y-2">
              <Label htmlFor="ftx-lo-email">Liaison Officer Email</Label>
              <Input
                id="ftx-lo-email"
                type="email"
                placeholder="e.g., lo.sparrow3@cae-aviation.com"
                value={ftxForm.loEmail}
                onChange={(e) => setFtxForm(prev => ({ ...prev, loEmail: e.target.value }))}
              />
              <p className="text-xs text-slate-500">
                Email address for the "Send LO" function in the briefing form.
              </p>
            </div>

            {/* Route Files Link - Multiple Routes Support */}
            <div className="space-y-2">
              <Label>Flight Lines (KML/KMZ Files)</Label>
              <p className="text-xs text-slate-500 mb-2">
                Add one or more flight line files for this FTX designator.
              </p>
              
              {/* Display existing route files */}
              {ftxForm.routeFiles && ftxForm.routeFiles.length > 0 && (
                <div className="space-y-2 mb-3">
                  {ftxForm.routeFiles.map((routeFileName, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-slate-50 p-2 rounded-md">
                      <Map className="w-4 h-4 text-emerald-600" />
                      <span className="flex-1 text-sm font-medium">{routeFileName}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const route = adminData.routes?.find(r => (r.name || r) === routeFileName);
                          if (route) handleLoadAirportsFromRoute(route);
                        }}
                        className="h-7 px-2"
                      >
                        <Navigation className="w-3 h-3 mr-1" />
                        Load Airports
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setFtxForm(prev => ({
                            ...prev,
                            routeFiles: prev.routeFiles.filter((_, i) => i !== idx),
                            routeFile: prev.routeFiles.filter((_, i) => i !== idx)[0] || ""
                          }));
                        }}
                        className="h-7 px-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Add new route file */}
              <div className="flex gap-2">
                <Select 
                  value=""
                  onValueChange={(value) => {
                    if (value && value !== "none" && !ftxForm.routeFiles.includes(value)) {
                      setFtxForm(prev => ({
                        ...prev,
                        routeFiles: [...prev.routeFiles, value],
                        routeFile: prev.routeFiles.length === 0 ? value : prev.routeFile
                      }));
                    }
                  }}
                >
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Add a flight line file..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- Select a file --</SelectItem>
                    {adminData.routes?.filter(r => !ftxForm.routeFiles.includes(r.name || r)).map((route, i) => (
                      <SelectItem key={i} value={route.name || route}>
                        {route.name || route} {route.detectedAirports?.length > 0 ? `(${route.detectedAirports.length} airports)` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Upload new route file */}
              <div className="flex items-center gap-2 mt-2">
                <span className="text-xs text-slate-400">or</span>
                <input
                  type="file"
                  accept=".kml,.kmz"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    
                    const validExtensions = ['.kml', '.kmz'];
                    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
                    
                    if (!validExtensions.includes(ext)) {
                      toast.error("Please upload a KML or KMZ file");
                      return;
                    }
                    
                    setUploadingFile(true);
                    const formData = new FormData();
                    formData.append('file', file);
                    
                    try {
                      const response = await axios.post(`${API}/admin/upload-route`, formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                      });
                      
                      // Add to routes list
                      setAdminData(prev => ({
                        ...prev,
                        routes: [...prev.routes, response.data]
                      }));
                      
                      // Add to routeFiles array
                      setFtxForm(prev => ({
                        ...prev,
                        routeFiles: [...prev.routeFiles, response.data.name],
                        routeFile: prev.routeFiles.length === 0 ? response.data.name : prev.routeFile
                      }));
                      
                      // Load detected airports if this is the first route
                      const detected = response.data.detectedAirports || [];
                      if (detected.length > 0 && ftxForm.routeFiles.length === 0) {
                        setFtxForm(prev => ({
                          ...prev,
                          airports: detected.map(a => ({
                            icao: a.icao,
                            name: a.name,
                            lat: a.lat.toString(),
                            lon: a.lon.toString()
                          }))
                        }));
                        toast.success(`Uploaded! Found ${detected.length} airports within 20 NM`);
                      } else {
                        toast.success("Route file added");
                      }
                    } catch (error) {
                      toast.error("Failed to upload route file");
                    } finally {
                      setUploadingFile(false);
                      e.target.value = ''; // Reset input
                    }
                  }}
                  className="hidden"
                  id="ftx-route-upload"
                  disabled={uploadingFile}
                />
                <label
                  htmlFor="ftx-route-upload"
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md cursor-pointer transition-colors ${
                    uploadingFile 
                      ? 'bg-slate-100 text-slate-400 cursor-not-allowed' 
                      : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200'
                  }`}
                >
                  {uploadingFile ? (
                    <>
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-3 h-3" />
                      Upload KML/KMZ
                    </>
                  )}
                </label>
              </div>
              
              <p className="text-xs text-slate-500">
                Upload a route file to auto-detect airports within 20 NM, or select an existing one.
              </p>
            </div>

            {/* Airports */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Airports on Route *</Label>
                <Button type="button" variant="outline" size="sm" onClick={handleAddAirport}>
                  <Plus className="w-3 h-3 mr-1" />
                  Add Airport
                </Button>
              </div>
              <p className="text-xs text-slate-500">
                Define airports along the route for weather predictions. The first airport is typically the departure.
              </p>
              
              <div className="space-y-3">
                {ftxForm.airports.map((airport, index) => (
                  <div key={index} className="p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-slate-500">Airport {index + 1}</span>
                      {ftxForm.airports.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveAirport(index)}
                          className="p-1 text-red-500 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      <div>
                        <Label className="text-xs">ICAO *</Label>
                        <Input
                          placeholder="EBOS"
                          value={airport.icao}
                          onChange={(e) => handleAirportChange(index, 'icao', e.target.value.toUpperCase())}
                          className="uppercase"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Name</Label>
                        <Input
                          placeholder="Ostend"
                          value={airport.name}
                          onChange={(e) => handleAirportChange(index, 'name', e.target.value)}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Latitude</Label>
                        <Input
                          placeholder="51.1989"
                          value={airport.lat}
                          onChange={(e) => handleAirportChange(index, 'lat', e.target.value)}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Longitude</Label>
                        <Input
                          placeholder="2.8622"
                          value={airport.lon}
                          onChange={(e) => handleAirportChange(index, 'lon', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Video & External Aircraft Settings */}
            <div className="space-y-4 border-t pt-4">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <Monitor className="w-4 h-4" />
                Video & External Aircraft Settings
              </div>
              
              <div className="space-y-2">
                <Label>Video Stream URL</Label>
                <Input
                  placeholder="e.g., https://srv-ops.nsas.tech:5444/live/streams/eagle7.m3u8"
                  value={ftxForm.videoStreamUrl}
                  onChange={(e) => setFtxForm(prev => ({ ...prev, videoStreamUrl: e.target.value }))}
                />
                <p className="text-xs text-slate-500">HLS stream URL for the Ops Dashboard video player</p>
              </div>

              <div className="space-y-3 bg-amber-50 rounded-lg p-3 border border-amber-200">
                <div className="flex items-center gap-2 text-sm font-medium text-amber-800">
                  <AlertTriangle className="w-4 h-4" />
                  External Aircraft (Subcontractor)
                </div>
                
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="isExternal"
                    checked={ftxForm.isExternal}
                    onChange={(e) => setFtxForm(prev => ({ 
                      ...prev, 
                      isExternal: e.target.checked,
                      excludeFromSkylegs: e.target.checked ? true : prev.excludeFromSkylegs
                    }))}
                    className="w-4 h-4 rounded"
                  />
                  <Label htmlFor="isExternal" className="cursor-pointer text-sm">
                    This is an external/subcontractor aircraft
                    <span className="text-xs text-slate-500 block">No crew or time data available from SkyLegs</span>
                  </Label>
                </div>

                {ftxForm.isExternal && (
                  <div className="space-y-2 pl-7">
                    <Label className="text-sm">ADS-B ICAO Hex Code</Label>
                    <Input
                      placeholder="e.g., a3b771"
                      value={ftxForm.adsbIcaoHex}
                      onChange={(e) => setFtxForm(prev => ({ ...prev, adsbIcaoHex: e.target.value.toLowerCase() }))}
                      className="lowercase font-mono"
                    />
                    <p className="text-xs text-slate-500">
                      Used to detect if this aircraft is flying via ADS-B Exchange.
                      Find this at <a href="https://globe.adsbexchange.com" target="_blank" rel="noopener noreferrer" className="text-amber-600 underline">globe.adsbexchange.com</a>
                    </p>
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="excludeFromWeather"
                    checked={ftxForm.excludeFromWeather}
                    onChange={(e) => setFtxForm(prev => ({ ...prev, excludeFromWeather: e.target.checked }))}
                    className="w-4 h-4 rounded"
                  />
                  <Label htmlFor="excludeFromWeather" className="cursor-pointer text-sm">
                    Exclude from weather briefings
                    <span className="text-xs text-slate-500 block">Don't include in auto-generated weather reports</span>
                  </Label>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="excludeFromSkylegs"
                    checked={ftxForm.excludeFromSkylegs}
                    onChange={(e) => setFtxForm(prev => ({ ...prev, excludeFromSkylegs: e.target.checked }))}
                    className="w-4 h-4 rounded"
                    disabled={ftxForm.isExternal}
                  />
                  <Label htmlFor="excludeFromSkylegs" className={`cursor-pointer text-sm ${ftxForm.isExternal ? 'opacity-50' : ''}`}>
                    Exclude from SkyLegs polling
                    <span className="text-xs text-slate-500 block">Don't query SkyLegs API for this designator</span>
                  </Label>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFtxDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveFtx}>
              {editingFtx !== null ? 'Update' : 'Create'} FTX Designator
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Aircraft Tracker Dialog */}
      <Dialog open={showAircraftDialog} onOpenChange={setShowAircraftDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingAircraft ? 'Edit Aircraft' : 'Add Aircraft to Tracker'}
            </DialogTitle>
            <DialogDescription>
              {editingAircraft 
                ? 'Update the aircraft tracking configuration'
                : 'Add a new aircraft to track via ADS-B Exchange'
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="callsign">Callsign / Designator *</Label>
              <Input
                id="callsign"
                placeholder="e.g., EAGLE 7"
                value={aircraftForm.callsign}
                onChange={(e) => setAircraftForm({ ...aircraftForm, callsign: e.target.value })}
              />
              <p className="text-xs text-slate-500">Display name for the aircraft</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="registration">Registration *</Label>
              <Input
                id="registration"
                placeholder="e.g., N339CA"
                value={aircraftForm.registration}
                onChange={(e) => setAircraftForm({ ...aircraftForm, registration: e.target.value.toUpperCase() })}
                className="uppercase"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="icao_hex">ICAO Hex Code *</Label>
              <Input
                id="icao_hex"
                placeholder="e.g., a3b771"
                value={aircraftForm.icao_hex}
                onChange={(e) => setAircraftForm({ ...aircraftForm, icao_hex: e.target.value.toLowerCase() })}
                className="lowercase font-mono"
                disabled={!!editingAircraft}
              />
              <p className="text-xs text-slate-500">
                24-bit ICAO address (6 hex characters). Find this at{' '}
                <a href="https://globe.adsbexchange.com" target="_blank" rel="noopener noreferrer" className="text-orange-600 underline">
                  globe.adsbexchange.com
                </a>
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="color">Marker Color</Label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  id="color"
                  value={aircraftForm.color}
                  onChange={(e) => setAircraftForm({ ...aircraftForm, color: e.target.value })}
                  className="w-12 h-10 rounded cursor-pointer border border-slate-200"
                />
                <Input
                  value={aircraftForm.color}
                  onChange={(e) => setAircraftForm({ ...aircraftForm, color: e.target.value })}
                  className="flex-1 font-mono"
                  placeholder="#FF6B00"
                />
              </div>
              <p className="text-xs text-slate-500">Color for the aircraft marker on the map</p>
            </div>

            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              <input
                type="checkbox"
                id="active"
                checked={aircraftForm.active}
                onChange={(e) => setAircraftForm({ ...aircraftForm, active: e.target.checked })}
                className="w-4 h-4 rounded"
              />
              <Label htmlFor="active" className="cursor-pointer">
                Active tracking
                <span className="text-xs text-slate-500 block">Enable to include in live tracking</span>
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAircraftDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveAircraft} 
              disabled={savingAircraft || !aircraftForm.icao_hex || !aircraftForm.registration || !aircraftForm.callsign}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {savingAircraft ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
              ) : (
                editingAircraft ? 'Update Aircraft' : 'Add Aircraft'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
